import { create } from 'zustand';
import { ApiError, api, loadToken, saveToken, setSessionRefusedHandler, type RegisterInput } from './api.js';
import { RpgDeskSocket, type SocketStatus } from './socket.js';
import { handleNdbMessage, resetDocs, resubscribeAll, setNdbIdentity, setNdbSender } from './plugins/ndbClient.js';
import { TOO_LARGE } from './ndb.js';
import { applyGlobalSettings, loadSettings, saveSettings, type AppSettings } from './settings.js';
import { noticeMessage, noticeRoom } from './notify.js';
import { toast } from './ui/Toaster.js';
import { POPOUT } from './windowMode.js';
import { onWindowMessage, openPopout, postWindows } from './room/windows.js';
import { mainChatHere, newChatRunId } from './room/chatRun.js';
import { newLayoutId, type ModuleClassId } from './room/layout.js';
import { CURRENT_TOS_VERSION, EMPTY_MEDIA, FRIENDS_LIMITS, NDB_GROUPS, OFFLINE_PRESENCE, barChanges, canSeeBarNumbers, deskFileRefId, nextGameSessionNumber, roleOfModes, serverText } from './shared.js';
import { useFriendsUi } from './social/friendsUi.js';
import { hasRoomForWindow, maxWindows } from './social/friendsModel.js';
import { isMobileNow } from './ui/mobile.js';
import { BackgroundRoom } from './room/backgroundRooms.js';
import { MAX_OPEN_ROOMS, countUnread, described, markRead, mentions, withClock, withRoom, withoutRoom, type OpenRoom } from './room/openRooms.js';
import { translate, useLanguage } from './i18n/index.js';
import { localizePlugins } from './plugins/localizePlugins.js';
import { rollProblem } from './dice/rollCheck.js';
import { animatesRoll, diceOn } from './dice/stage.js';
import { reportProblem } from './monitoring.js';
import { noteServerTime } from './serverClock.js';
import { planCheckDelay, uploadRefusal } from './plans/planModel.js';
// o envio do anexo do chat leva as medidas e a miniatura (docs/spec/desk-cloud.md); files.ts só usa a store dentro das funções
import { sendToCloud } from './files.js';
import type { UseAction } from './cloud/cloudModel.js';
import type {
  EmailConfirmationView,
  AudioRepeat,
  AudioSfxEvent,
  AudioState,
  ChatChannel,
  ClientMessage,
  ChatMessage,
  GameSessionAction,
  LibraryItem,
  RoomPlugin,
  ChatPersona,
  ContactList,
  ContactState,
  ContactView,
  ConversationView,
  CreateRoomInput,
  ImsAccess,
  ImsMessage,
  ImsPeer,
  ImsPrivacy,
  Privacy,
  PresenceFlag,
  ProfileUpdate,
  PvtGroup,
  Room,
  RoomCard,
  RoomMember,
  RoomRole,
  RollResult,
  RtcMedia,
  RtcSignal,
  PlanCeilings,
  PlanView,
  PublicPlanCatalog,
  ServerMessage,
  User,
  UserProfile,
  UserStatus,
} from './shared.js';

/** Menção ao meu nome e mensagem privada recebida tocam o aviso da mesa. */
function noticeChat(m: ChatMessage, state: { user: User | null; members: Record<string, RoomMember>; room: Room | null; settings: AppSettings }): void {
  const me = state.user;
  if (!me || m.userId === me.id || m.kind === 'system') return;
  const label = state.room?.name ?? 'RPG Desk';
  if (m.channel) {
    noticeRoom(state.settings, 'pvt', label);
    return;
  }
  if (mentions(m.text, [me.displayName, state.members[me.id]?.nick])) noticeRoom(state.settings, 'mention', label);
}

/** Cores e textura (com o zoom) dos meus dados 3D, se personalizei (vão junto da rolagem para os outros verem). */
const diceStyle = (settings: AppSettings) => {
  if (!settings.diceCustom) return {};
  const texture = deskFileRefId(settings.diceTexture) ? settings.diceTexture : null;
  const scale = settings.diceTextureScale !== 1 ? { textureScale: settings.diceTextureScale } : {};
  return { style: { body: settings.diceBody, number: settings.diceNumber, ...(texture ? { texture, ...scale } : {}) } };
};

/**
 * A minha rolagem voltou sem a textura que mandei (o servidor a recusou: foi para a lixeira, saiu do
 * Desk Cloud, passou das regras): Configurações → Dados avisa. A que volta com ela tira o aviso.
 */
const textureEcho = (state: AppState, roll: RollResult): Partial<AppState> => {
  const { diceCustom, diceTexture } = state.settings;
  if (roll.userId !== state.user?.id || !roll.style || !diceCustom || !deskFileRefId(diceTexture)) return {};
  const refused = roll.style.texture === diceTexture ? null : diceTexture;
  return refused === state.diceTextureRefused ? {} : { diceTextureRefused: refused };
};
/** Meus efeitos especiais, se mudei o padrão (sem eles, quem vê usa o padrão do RPG Desk). */
const diceEffects = (settings: AppSettings) => (settings.diceEffects ? { effects: settings.diceEffects } : {});

type SignalListener = (fromUserId: string, signal: RtcSignal) => void;
const signalListeners = new Set<SignalListener>();

/** Pings de cena (efêmeros: não passam pelo estado, vão direto para quem desenha a cena). */
/**
 * Uma ação de "Usar na mesa" à espera (docs/spec/desk-cloud.md): a mesa, o arquivo e a ação. `itemId`
 * é a cena que deve fazê-la (a cena nova, depois de criada); `knownScenes` e `enqueued` guardam o
 * passo em que a cena nova e a música estão.
 */
export type PendingFileUse = {
  nonce: number;
  roomId: string;
  fileId: string;
  name: string;
  action: UseAction;
  itemId?: string;
  knownScenes?: string[];
  enqueued?: boolean;
};

export type ScenePing = { itemId: string; userId: string; x: number; y: number; focus: boolean };
const pingListeners = new Set<(ping: ScenePing) => void>();
export function onScenePing(listener: (ping: ScenePing) => void): () => void {
  pingListeners.add(listener);
  return () => pingListeners.delete(listener);
}

/** Porta trancada que alguém tentou abrir (vai para quem tentou e para os mestres). */
export type SceneDoorLocked = { itemId: string; wallId: string; userId: string };
const doorListeners = new Set<(event: SceneDoorLocked) => void>();
export function onSceneDoorLocked(listener: (event: SceneDoorLocked) => void): () => void {
  doorListeners.add(listener);
  return () => doorListeners.delete(listener);
}

/** Quem não é mestre mudou o invisível de um token (BRAZ-24; vai para os mestres). */
export type SceneInvisibleNotice = { itemId: string; tokenId: string; userId: string; change: 'on' | 'viewers' | 'off' };
const invisibleListeners = new Set<(event: SceneInvisibleNotice) => void>();
export function onSceneInvisible(listener: (event: SceneInvisibleNotice) => void): () => void {
  invisibleListeners.add(listener);
  return () => invisibleListeners.delete(listener);
}

/** Sinalização WebRTC recebida, um sinal por chamada, sem perder nenhum. */
export function onRtcSignal(listener: SignalListener): () => void {
  signalListeners.add(listener);
  return () => signalListeners.delete(listener);
}

const MAX_MESSAGES = 600;
/**
 * Quantas rolagens a interface mantém na memória: as mesmas que o chat (cada resumo de rolagem no log
 * acha nelas o detalhe dos dados). Com menos, as rolagens antigas ainda à vista no log perdiam o
 * detalhe e mudavam de aparência.
 */
const MAX_ROLLS = MAX_MESSAGES;
/** rolagens de plugins esperando o resultado (ref → resolve) */
const rollWaiters = new Map<string, (roll: RollResult) => void>();
/**
 * Quanto o resultado espera, no máximo, pelos dados pararem (ms): a rolagem que a animação não soltou
 * até aqui aparece assim mesmo. Fica abaixo do prazo de `rollForPlugin` (30 s), para a ficha não
 * desistir de uma rolagem que chegou.
 */
const REVEAL_CAP_MS = 20_000;
/**
 * Rolagens com o resultado guardado até os dados pararem nesta tela (docs/spec/dice-3d.md, "Resultado
 * depois da animação"), por id: a mesa, o teto e a ficha que espera o resultado, se foi ela que rolou.
 */
const held = new Map<string, { roomId: string; timer: ReturnType<typeof setTimeout>; release: (() => void) | null }>();
/** Na janela própria: as rolagens que a principal já soltou (o aviso pode chegar antes da rolagem). */
const shownByMain = new Set<string>();

/** A principal soltou o resultado: as janelas próprias da mesa o mostram junto. */
function rollShown(roomId: string, rollId: string): void {
  if (!POPOUT) postWindows({ t: 'roll-shown', roomId, rollId });
}

/** Solta todas as rolagens guardadas, sem mexer no estado (quem chama zera `rollingDice`). */
function releaseAllRolls(): void {
  for (const [rollId, { roomId, timer, release }] of held) {
    clearTimeout(timer);
    release?.();
    rollShown(roomId, rollId);
  }
  held.clear();
  shownByMain.clear();
}
/** webhooks e instalações de plugins web esperando a resposta do servidor (ref → resolve) */
const webhookWaiters = new Map<string, (result: WebhookSendResult) => void>();
const installWaiters = new Map<string, (result: { ok: boolean; error: string | null }) => void>();

/** Manda o pedido de instalação com um `ref` e espera o `plugin.installResult` dele (até um minuto). */
function awaitInstall(send: (ref: string) => void): Promise<{ ok: boolean; error: string | null }> {
  const ref = newRef('i');
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      installWaiters.delete(ref);
      resolve({ ok: false, error: translate(useLanguage.getState().language, 'api.noAnswer') });
    }, 60_000);
    installWaiters.set(ref, (result) => {
      clearTimeout(timer);
      resolve(result);
    });
    send(ref);
  });
}

/** Resultado de um webhook de plugin web (docs/spec/web-plugins.md, "Internet"). */
export type WebhookSendResult = { ok: boolean; status: number; body: unknown; error: string | null };
export type PluginWebhooks = {
  hooks: { name: string; url: string; who: 'gm' | 'everyone' }[];
  log: { at: number; nick: string; name: string; ok: boolean; status: number; error: string | null }[];
};

const newRef = (prefix: string) => `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

/** O resultado do link do e-mail da confirmação: confirmado, vencido, sem valor (usado ou trocado) ou sem rede. */
export type EmailLinkNotice = 'confirmed' | 'expired' | 'invalid' | 'failed';

export type AppState = {
  token: string | null;
  user: User | null;
  /** administrador do servidor: vê a área de administração (docs/spec/admin.md) */
  isAdmin: boolean;
  /** a conta não aceitou a versão atual dos termos: o aviso do novo aceite fica na tela (docs/spec/termos.md) */
  tosPending: boolean;
  /**
   * A conta espera a confirmação do e-mail (docs/spec/email.md): a tela de espera fica no lugar do app,
   * sem a conexão, as mesas e os amigos. null: confirmada, ou fora da conta.
   */
  emailConfirmation: EmailConfirmationView | null;
  /**
   * O resultado do link do e-mail da confirmação aberto nesta aba: a tela de entrada mostra num
   * diálogo; com a conta aberta, vira um aviso rápido.
   */
  emailLinkNotice: EmailLinkNotice | null;
  dismissEmailLinkNotice: () => void;
  /**
   * O plano da conta (docs/spec/planos.md): qual é, até quando e os direitos que valem agora, já com os
   * tetos do servidor; null fora da conta. Quem decide é o servidor: aqui é para mostrar e avisar antes.
   */
  plan: PlanView | null;
  /** os tetos do servidor (o maior arquivo): a comparação de planos os aplica a todos (docs/spec/planos.md) */
  planCeilings: PlanCeilings;
  /** o catálogo dos planos (os direitos e os nomes editados; docs/spec/planos.md, "Editar os planos") */
  planCatalog: PublicPlanCatalog | null;
  /**
   * Um administrador encerrou a sessão (suspendeu a conta, trocou a senha ou a apagou): a tela de
   * entrada mostra o porquê. `detail`: o motivo da suspensão.
   */
  signedOutNotice: { reason: 'suspended' | 'password' | 'deleted'; detail: string } | null;
  dismissSignedOutNotice: () => void;
  /** cartões de sala para a tela de descoberta (com favorito/voto/presença) */
  rooms: RoomCard[];
  /** id da sala em que estamos conectados agora (a da tela) */
  activeRoomId: string | null;
  /**
   * Mesas abertas nesta janela, na ordem dos botões da barra lateral: a da tela e as de fundo,
   * que mantêm a pessoa dentro delas e contam as não lidas (docs/spec/open-rooms.md).
   */
  openRooms: OpenRoom[];
  /**
   * Sala que pretendemos estar (com a senha, quando houver). Serve para dois
   * casos: entrar depois do `hello` e reentrar sozinho após uma reconexão.
   */
  pendingJoin: { roomId: string; password: string | null } | null;
  /** o que o servidor oferece além do básico (vem no `hello`) */
  serverFeatures: { randomOrg: boolean; presence: boolean; status: boolean; imsAnyone: boolean; gameSessions: boolean; sessionRatings: boolean; chatDelete: boolean };
  room: Room | null;
  /** conta os `ready` (entrar ou reconectar na mesa): quem precisa refazer algo depois de uma queda observa */
  readySeq: number;
  users: User[];
  /** papel, modos e apelido de quem está na sala, por userId */
  members: Record<string, RoomMember>;
  /** quem está digitando agora (userId → instante do aviso) */
  typing: Record<string, number>;
  /**
   * Pedido para a área de trabalho abrir a ficha de um item da biblioteca. `window`: numa janela
   * própria nova; `popWindow`: essa janela já foi aberta (por uma janela própria), com esse id.
   * `within`: a janela própria de onde veio o pedido, onde a aba abre (docs/spec/room-layout.md).
   */
  openSheet: { itemId: string; window?: boolean; popWindow?: string; within?: string; nonce: number } | null;
  requestOpenSheet: (itemId: string, opts?: { window?: boolean; popWindow?: string; within?: string }) => void;
  /** uma aba pedida de fora dos menus (o relógio abre a aba Sessões): a mesa da tela a abre ou a põe à vista */
  openModule: { classId: ModuleClassId; nonce: number } | null;
  requestOpenModule: (classId: ModuleClassId) => void;
  /**
   * Levar a pessoa a uma sessão da aba Sessões, no bloco Avaliações (docs/spec/session-clock.md,
   * "Avaliações"): o ★ do relógio e o Ver dos avisos. Se a mesa não é a da tela, entra nela antes; a
   * mesa abre a aba quando estiver pronta, e a aba abre o cartão da sessão.
   */
  sessionFocus: { roomId: string; number: number; nonce: number; opened: boolean } | null;
  focusGameSession: (roomId: string, number: number) => void;
  /** a mesa abriu a aba para o pedido (`opened`), ou a aba já mostrou a sessão (`shown`, e o pedido acaba) */
  settleSessionFocus: (nonce: number, stage: 'opened' | 'shown') => void;
  /**
   * Linha para o chat principal rodar como se fosse digitada (botões do painel de macros): um chat só
   * a roda (`takeChatRun`), e só na mesa em que foi pedida (room/chatRun.ts).
   */
  chatRun: { id: string; roomId: string; text: string; nonce: number; at: number; done: boolean } | null;
  /**
   * Onde as boas-vindas da mesa entram no chat: na hora em que eu entrei (relógio do servidor),
   * como uma mensagem comum, que sobe com as novas (BRAZ-7). Reconectar à mesma mesa não muda.
   */
  welcome: { roomId: string; at: number } | null;
  runInChat: (text: string) => void;
  /** O chat que vai rodar a linha pede a vez: true só uma vez por `nonce`. */
  takeChatRun: (nonce: number) => boolean;
  /** biblioteca da sala (pastas e fichas), só o que eu vejo */
  library: LibraryItem[];
  /**
   * itens que eu via e o mestre ocultou (BRAZ-16): o que estava aberto deles vira um bloco preto
   * até voltarem (diferente de apagado, que mostra o aviso)
   */
  hiddenLibrary: string[];
  /** plugins instalados na mesa */
  plugins: RoomPlugin[];
  /** os plugins como o servidor manda; `plugins` é esta lista na língua da interface */
  pluginsRaw: RoomPlugin[];
  /** webhooks dos plugins web, como o servidor os mostra ao mestre (moduleId → endereços e envios) */
  pluginWebhooks: Record<string, PluginWebhooks>;
  /** grupos de conversa privada de que faço parte nesta mesa */
  groups: Record<string, PvtGroup>;
  /**
   * Pedido para a área de trabalho abrir uma conversa privada (mensagem recebida ou
   * menu "abrir conversa"). `focus`: ativar a aba; senão abre em segundo plano. `within`: a janela
   * própria de onde veio o pedido, onde a conversa abre.
   */
  openChannel: { channel: ChatChannel; focus: boolean; within?: string; nonce: number } | null;
  /** por que fui tirado da última sala (expulso/banido), para avisar na lista */
  removedNotice: { reason: 'kicked' | 'banned' | 'deleted'; byName: string } | null;
  messages: ChatMessage[];
  /** rolagens recentes; a última é a que a bandeja anima */
  rolls: RollResult[];
  /**
   * rolagens com os dados ainda rolando nesta tela: o chat mostra "…" no lugar do resultado até os
   * dados pararem (docs/spec/dice-3d.md, "Resultado depois da animação")
   */
  rollingDice: Record<string, true>;
  /** a textura dos meus dados que a última rolagem minha não levou (o servidor recusou), ou null */
  diceTextureRefused: string | null;
  /** estado do áudio compartilhado da sala */
  audio: AudioState | null;
  /** último efeito sonoro recebido */
  lastSfx: AudioSfxEvent | null;
  /** incrementa a cada efeito: dispara o som mesmo se for o mesmo arquivo de novo */
  sfxNonce: number;
  /** última sinalização WebRTC recebida (nonce força reprocessar iguais) */
  /** o que cada participante está publicando em voz/vídeo */
  peerMedia: Record<string, RtcMedia>;
  /* ---------------------------------------------------------------- amigos (docs/spec/friends.md) */
  contacts: ContactList | null;
  /** resultado da busca de usuários para adicionar */
  userSearch: { user: User; relation: ContactState | null }[];
  /** as conversas com cada pessoa (as das janelas abertas e as que receberam mensagem) */
  conversations: Record<string, { messages: ImsMessage[]; loaded: boolean }>;
  /**
   * com quem é cada conversa e se dá para mandar (vem com o histórico). É o que a janela usa para
   * quem não é amigo; antes de o histórico chegar, o que quem abriu a conversa já sabia da pessoa
   */
  dmPeers: Record<string, ImsPeer>;
  /** de quem recebo mensagens diretas (Configurações → Conta e segurança; null até ler) */
  privacy: Privacy | null;
  /** quem está digitando para mim, e até quando vale o aviso */
  dmTyping: Record<string, number>;
  /** o status que escolhi na minha foto (vem no `hello`; até lá, null) */
  myStatus: UserStatus | null;
  /* ---------------------------------------------------------------- perfil */
  /** perfil que está sendo exibido no popup (meu ou de outra pessoa) */
  profile: UserProfile | null;
  profileUserId: string | null;
  /**
   * O meu perfil, para a seção Perfil das Configurações: à parte do popup, para abrir o de outra
   * pessoa com a seção aberta não trocar um pelo outro.
   */
  myProfile: UserProfile | null;
  loadMyProfile: () => Promise<void>;
  /** o popup de perfil está aberto (FC.Popup.UserProfilePopup) */
  profileOpen: boolean;
  closeProfile: () => void;
  /* ---------------------------------------------------------------- Desk Cloud */
  /** espaço ocupado e cota do Desk Cloud (bytes) */
  fileUsage: { used: number; quota: number } | null;
  /** sobe a cada `files.changed` (outra aba, outro aparelho, um envio): quem mostra arquivos relê */
  filesVersion: number;
  /** sobe a cada `room.files` de uma mesa: o painel Arquivos dela relê */
  roomFilesVersion: Record<string, number>;
  /** sobe a cada mudança do relógio ou do registro das sessões de uma mesa: a aba Sessões relê */
  gameSessionsVersion: Record<string, number>;
  /** sobe a cada `chat.removed` de uma mesa: o /audit dela relê (docs/spec/chat-delete.md) */
  chatRemovedVersion: Record<string, number>;
  status: SocketStatus;
  busy: boolean;
  error: string | null;

  initialize: () => Promise<void>;
  register: (input: RegisterInput, keepSignedIn?: boolean) => Promise<boolean>;
  /** `account` aceita o nome da conta ou o e-mail. */
  login: (account: string, password: string, keepSignedIn?: boolean) => Promise<boolean>;
  logout: () => Promise<void>;
  /** Aceita a versão atual dos termos; devolve o problema, ou null se deu certo. */
  acceptTos: () => Promise<string | null>;
  /** Relê a espera pela confirmação do e-mail; confirmada (noutra aba, noutro aparelho), entra no app. */
  refreshEmailConfirmation: () => Promise<void>;
  /** Pede outro envio do link; devolve o problema, ou null se deu certo. */
  resendConfirmation: () => Promise<string | null>;
  /** Corrige o e-mail que espera a confirmação; devolve o problema, ou null se deu certo. */
  changePendingEmail: (email: string) => Promise<string | null>;
  /** O link do e-mail da confirmação (`?confirm-email=`): confirma e guarda o resultado em `emailLinkNotice`. */
  confirmEmailLink: (code: string) => Promise<void>;
  refreshRooms: () => Promise<void>;
  createRoom: (input: Partial<CreateRoomInput> & { name: string }) => Promise<void>;
  /** Entra numa mesa; se outra estiver na tela, ela vai para o fundo (continua aberta). */
  joinRoom: (roomId: string, password?: string | null) => void;
  /** Sai de uma mesa aberta (a da tela ou uma de fundo). */
  leaveOpenRoom: (roomId: string) => void;
  /** Recarregou a página: reabre as mesas desta janela, com `activeId` na tela. */
  reopenRooms: (roomIds: string[], activeId: string | null) => void;
  toggleFavorite: (roomId: string) => Promise<void>;
  voteRoom: (roomId: string, value: -1 | 0 | 1) => Promise<void>;
  leaveRoom: (farewell?: string) => void;
  setRole: (userId: string, role: RoomRole) => void;
  setFlag: (userId: string, flag: PresenceFlag, on: boolean) => void;
  kick: (userId: string, ban: boolean) => void;
  setNick: (nick: string | null) => void;
  /** personagem principal (ficha da biblioteca) do meu cartão; `userId` (mestre): o de outra pessoa, entre as fichas dela */
  setMainCharacter: (itemId: string | null, userId?: string) => void;
  /** barrinha do cartão (minha ou, sendo mestre, de outra pessoa); índice a partir de 0 */
  setCardBar: (userId: string, index: number, value: number, max: number, color?: string) => void;
  setCardLine: (userId: string, index: number, text: string) => void;
  setModerated: (on: boolean) => void;
  sendTyping: (typing: boolean) => void;
  dismissRemovedNotice: () => void;
  sendChat: (text: string, kind?: 'chat' | 'action' | 'laugh' | 'ooc', persona?: ChatPersona, channel?: ChatChannel) => void;
  rollDice: (expression: string, label?: string | null, channel?: ChatChannel) => void;
  openPrivate: (channel: ChatChannel, focus?: boolean, within?: string) => void;
  /** rolagem pedida por plugin: resolve com o resultado do servidor, quando os dados param nesta tela */
  rollForPlugin: (expression: string, label: string | null) => Promise<RollResult>;
  /** os dados da rolagem pararam (ou ela não anima): o resultado aparece; sem efeito se não estava guardada */
  revealRoll: (rollId: string) => void;
  /** instala um plugin web já enviado ao Desk Cloud; resolve quando o servidor responde */
  installWebPlugin: (fileId: string) => Promise<{ ok: boolean; error: string | null }>;
  /** instala (ou atualiza) um plugin oficial do catálogo na mesa aberta; resolve quando o servidor responde */
  installOfficialPlugin: (officialId: string) => Promise<{ ok: boolean; error: string | null }>;
  /** webhook de um plugin web, enviado pelo servidor */
  sendWebhook: (moduleId: string, name: string, data: unknown) => Promise<WebhookSendResult>;
  /** pede ao servidor os webhooks do plugin (só mestres) */
  loadWebhooks: (moduleId: string) => void;
  setWebhook: (moduleId: string, name: string, url: string | null, who: 'gm' | 'everyone') => void;
  /** linha só local no chat (chat:escrever dos plugins) */
  addLocalLine: (text: string) => void;
  /** envia uma mensagem qualquer do protocolo (módulos de plugin, biblioteca) */
  sendRaw: (message: ClientMessage) => void;
  createLibraryItem: (input: { kind: LibraryItem['kind']; name: string; parentId?: string | null; dataType?: string | null; ownerId?: string | null }) => void;
  renameLibraryItem: (itemId: string, name: string) => void;
  moveLibraryItem: (itemId: string, parentId: string | null) => void;
  deleteLibraryItem: (itemId: string) => void;
  /** mestre: passa a ficha para alguém da mesa (null: ninguém, só os mestres editam) */
  setLibraryOwner: (itemId: string, ownerId: string | null) => void;
  /** mestre: torna o item visível para todos ou o oculta dos jogadores (BRAZ-16) */
  setLibraryVisible: (itemId: string, visible: boolean) => void;
  setDockActive: (itemId: string, active: boolean) => void;
  /**
   * envia o arquivo ao Desk Cloud e compartilha no chat (canal opcional). False quando não saiu (o
   * erro fica em `error`): a legenda não foi junto
   */
  attachFile: (file: File, channel?: ChatChannel, caption?: string) => Promise<boolean>;
  /** manda no chat um arquivo que já está no Desk Cloud (o servidor o liga à mesa), com a legenda opcional */
  attachStoredFile: (fileId: string, channel?: ChatChannel, caption?: string) => void;
  /**
   * O "Usar na mesa" do Desk Cloud (docs/spec/desk-cloud.md): a ação espera a mesa (e a cena) abrir, e
   * quem a faz a pega com `takePendingFileUse`, que só dá certo uma vez por `nonce`.
   */
  pendingFileUse: PendingFileUse | null;
  setPendingFileUse: (use: PendingFileUse | null) => void;
  takePendingFileUse: (nonce: number) => boolean;
  createGroup: (userIds: string[]) => void;
  inviteToGroup: (groupId: string, userIds: string[]) => void;
  partGroup: (groupId: string) => void;
  enqueueAudio: (url: string, title: string, kind?: 'music' | 'sfx') => void;
  controlAudio: (action: 'play' | 'pause' | 'stop' | 'next' | 'prev' | 'seek' | 'select' | 'ended', extra?: { positionSeconds?: number; trackId?: string }) => void;
  removeAudio: (trackId: string) => void;
  clearAudio: () => void;
  setAudioVolume: (volume: number) => void;
  setAudioRepeat: (repeat: AudioRepeat) => void;
  triggerSfx: (trackId: string) => void;
  /**
   * Começa, pausa, volta ou encerra a sessão de jogo da mesa da tela (docs/spec/session-clock.md).
   * Só com a conexão de pé: uma pausa guardada para quando a conexão voltar valeria na hora errada.
   */
  controlGameSession: (action: GameSessionAction) => boolean;
  /**
   * Um mestre apaga uma linha do chat da mesa, ou o chat inteiro (docs/spec/chat-delete.md). Só com a
   * conexão de pé; a linha some quando o `chat.removed` chega.
   */
  deleteChatLine: (id: string) => boolean;
  clearChat: () => boolean;
  sendSignal: (targetUserId: string, signal: RtcSignal) => void;
  sendMedia: (media: RtcMedia) => void;
  /* ---------------------------------------------------------------- amigos */
  refreshContacts: () => Promise<void>;
  searchUsers: (query: string) => Promise<void>;
  /** pedido de amizade, pela pessoa ou pelo nome digitado; true se foi enviado */
  requestContact: (target: { userId: string } | { displayName: string }, message: string | null) => Promise<boolean>;
  contactAction: (
    userId: string,
    action: 'accept' | 'reject' | 'block' | 'unblock' | 'hide' | 'unhide',
  ) => Promise<void>;
  /**
   * abre (ou traz para a frente) a janela de conversa com alguém, amigo ou não. `seed`: quem é a
   * pessoa, para a janela ter o nome antes de o histórico chegar
   */
  openChat: (userId: string, seed?: User) => void;
  /** busca o histórico da conversa (não marca lido) */
  loadConversation: (userId: string) => Promise<void>;
  /** a conversa foi vista: zera as não lidas dela */
  markConversationRead: (userId: string) => Promise<void>;
  sendDirectMessage: (userId: string, text: string) => Promise<boolean>;
  /** "Tirar da lista" uma conversa com quem não é amigo (some até a próxima mensagem) */
  dismissConversation: (userId: string) => Promise<void>;
  /** lê de quem recebo mensagens diretas */
  loadPrivacy: () => Promise<void>;
  /** muda de quem recebo mensagens diretas; true se gravou */
  setImsPrivacy: (ims: ImsPrivacy) => Promise<boolean>;
  /** "digitando…" para o amigo (só com a conexão aberta; não fica na fila) */
  sendDmTyping: (userId: string, typing: boolean) => void;
  /** esta janela ficou sem uso (ou voltou a ser usada) */
  reportAway: (away: boolean) => void;
  /** escolhe o meu status (Online, Ocupado ou Offline); sem conexão, avisa e não muda */
  setMyStatus: (status: UserStatus) => void;
  /** chama amigos para a mesa da tela; devolve quantos foram chamados */
  inviteFriends: (userIds: string[]) => Promise<number>;
  /* ---------------------------------------------------------------- perfil */
  /** carrega o perfil; `open: false` só busca (sem abrir o popup) */
  viewProfile: (userId: string, open?: boolean) => Promise<void>;
  /** Grava o perfil; false se não deu (o erro fica em `error`). */
  saveProfile: (patch: ProfileUpdate) => Promise<boolean>;
  voteUser: (userId: string, value: -1 | 0 | 1) => Promise<void>;
  /* ---------------------------------------------------------------- configurações */
  settings: AppSettings;
  updateSettings: (patch: Partial<AppSettings>) => void;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  deleteAccount: (password: string) => Promise<boolean>;
  /* ---------------------------------------------------------------- Desk Cloud */
  /** relê o espaço usado e a cota (o resumo do Desk Cloud) */
  refreshFiles: () => Promise<void>;
  /** troca o avatar (a imagem já reduzida) ou tira com null */
  setAvatar: (image: Blob | null) => Promise<boolean>;
  /** Foto da pessoa nesta mesa (null volta à do perfil); a sala recebe pelo `presence.updated`. */
  setRoomAvatar: (userId: string, image: Blob | null) => Promise<boolean>;
  deleteRoom: (roomId: string) => Promise<void>;
  dismissError: () => void;
};

/** A recusa do envio de uma mensagem direta, pelo código do servidor: o aviso que a janela mostra. */
const REFUSED: Record<string, ImsAccess> = { 'friends-only': 'friendsOnly', blocked: 'blockedByMe', forbidden: 'unavailable' };

export function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return translate(useLanguage.getState().language, 'api.unexpected');
}


/** Quanto o "digitando…" de alguém da mesa fica sem um aviso novo. */
export const ROOM_TYPING_TTL_MS = 15_000;

export const useApp = create<AppState>()((set, get) => {
  // preferências desta máquina, aplicadas já na subida (tema e fonte)
  const settingsIniciais = loadSettings();
  applyGlobalSettings(settingsIniciais);

  /** Os textos do próprio cliente, na língua escolhida. */
  const say = (key: string, params?: Record<string, string | number>) => translate(useLanguage.getState().language, key, params);

  /**
   * Troca o plano da conta (no login, no `/api/me` e quando um admin o muda): a cota da tela de arquivos
   * acompanha, e o vencimento é conferido na hora em que acontece. O servidor já não depende disso; é
   * para a barra e os avisos não mostrarem um plano vencido.
   */
  let planTimer: ReturnType<typeof setTimeout> | undefined;
  const applyPlan = (plan: PlanView | null): void => {
    set((state) => ({ plan, fileUsage: plan && state.fileUsage ? { ...state.fileUsage, quota: plan.entitlements.storageBytes } : state.fileUsage }));
    clearTimeout(planTimer);
    planTimer = undefined;
    const delay = planCheckDelay(plan, Date.now());
    if (delay === null) return;
    planTimer = setTimeout(() => {
      const token = get().token;
      if (!token) return;
      api.me(token).then(
        ({ plan: fresh }) => {
          if (get().token === token) applyPlan(fresh);
        },
        // sem rede: confere de novo mais tarde, com o que já se sabe
        () => {
          if (get().token === token) applyPlan(get().plan);
        },
      );
    }, delay);
  };

  /**
   * Aviso local quando as barrinhas de um cartão mudam ("Vida de Ana: -5"). Cada cliente
   * escreve o seu: quem não vê os números de uma barra recebe só o sinal ("-?").
   */
  function noticeBarChanges(before: RoomMember, after: RoomMember): void {
    const { room, user: me, members, users, settings } = get();
    if (!room || !me || !settings.barNotices) return;
    const tr = (key: string, params?: Record<string, string | number>) => translate(useLanguage.getState().language, key, params);
    const viewer = { userId: me.id, isGm: room.ownerId === me.id || !!members[me.id]?.modes.includes('gm') };
    const changes = barChanges(
      room.gameRules,
      before.bars,
      after.bars,
      (i) => canSeeBarNumbers(room.gameRules, i, viewer, after.userId),
      (i) => tr('users.card.bar', { n: i + 1 }),
      tr('users.card.maxDelta'),
    );
    if (!changes.length) return;
    const player = after.nick ?? users.find((u) => u.id === after.userId)?.displayName ?? '?';
    get().addLocalLine(changes.map((c) => tr('users.card.barChange', { bar: c.name, player, delta: c.text })).join('; '));
  }

  /* ------------------------------------------------------------ mesas abertas */

  /** Conexões das mesas de fundo, por sala. */
  const background = new Map<string, BackgroundRoom>();
  /** Boas-vindas de cada mesa aberta: voltar a uma mesa não as põe no fim de novo. */
  const welcomes = new Map<string, { roomId: string; at: number }>();
  /** Cada troca de mesa ganha um número: só a última chega a entrar. */
  let switchSeq = 0;
  /** Cada busca de contatos também: a resposta atrasada de uma busca antiga fica de fora. */
  let searchSeq = 0;
  /** O histórico pedido por último de cada conversa: o que chega atrasado fica de fora. */
  const conversationSeq = new Map<string, number>();
  /** Quantos `hello` esta conexão já teve: do segundo em diante, é uma reconexão. */
  let hellos = 0;
  /** O último "sem uso" informado (de novo a cada conexão, que começa sem saber). */
  let lastAway: boolean | null = null;

  /** Muda um amigo (ou escondido) na lista, se ele estiver lá. */
  function patchContact(userId: string, patch: (contact: ContactView) => ContactView): void {
    set((state) => {
      if (!state.contacts) return state;
      const map = (list: ContactView[]) => (list.some((c) => c.user.id === userId) ? list.map((c) => (c.user.id === userId ? patch(c) : c)) : list);
      return { contacts: { ...state.contacts, friends: map(state.contacts.friends), hidden: map(state.contacts.hidden) } };
    });
  }

  /** Muda as não lidas de uma conversa, seja de amigo ou das "Outras conversas". */
  function patchUnread(userId: string, unread: (count: number) => number): void {
    patchContact(userId, (c) => ({ ...c, unreadCount: unread(c.unreadCount) }));
    set((state) => {
      if (!state.contacts?.others.some((c) => c.user.id === userId)) return state;
      const others = state.contacts.others.map((c) => (c.user.id === userId ? { ...c, unreadCount: unread(c.unreadCount) } : c));
      return { contacts: { ...state.contacts, others } };
    });
  }

  /** Amigo (ou escondido) na lista. */
  function friendOf(userId: string): ContactView | undefined {
    const contacts = get().contacts;
    return contacts?.friends.find((c) => c.user.id === userId) ?? contacts?.hidden.find((c) => c.user.id === userId);
  }

  /**
   * A conversa com quem não é amigo vai para o alto das "Outras conversas", com a última mensagem.
   * Quem ainda não está lá entra se a gente souber quem é; senão, a lista vem de novo do servidor.
   */
  function touchOther(userId: string, dm: ImsMessage, user: User | undefined): void {
    const contacts = get().contacts;
    if (!contacts || friendOf(userId)) return;
    const current = contacts.others.find((c) => c.user.id === userId);
    const who = current?.user ?? user;
    if (!who) {
      void get().refreshContacts();
      return;
    }
    const lastMessage = { text: dm.text, at: dm.createdAt, fromMe: dm.fromId !== userId };
    const entry: ConversationView = { user: who, lastMessage, unreadCount: current?.unreadCount ?? 0 };
    set((state) => (state.contacts ? { contacts: { ...state.contacts, others: [entry, ...state.contacts.others.filter((c) => c.user.id !== userId)] } } : state));
  }

  /** Junta mensagens numa conversa, sem repetir (a mesma pode chegar pelo POST e pelo aviso). */
  function addMessages(userId: string, incoming: readonly ImsMessage[], loaded?: boolean): void {
    set((state) => {
      const current = state.conversations[userId] ?? { messages: [], loaded: false };
      const known = new Set(current.messages.map((m) => m.id));
      const fresh = incoming.filter((m) => !known.has(m.id));
      if (!fresh.length && (loaded === undefined || loaded === current.loaded)) return state;
      const messages = [...current.messages, ...fresh].sort((a, b) => a.createdAt - b.createdAt);
      return { conversations: { ...state.conversations, [userId]: { messages, loaded: loaded ?? current.loaded } } };
    });
  }

  /** A janela da conversa está à vista (expandida e com a página na frente)? */
  function conversationVisible(userId: string): boolean {
    if (typeof document !== 'undefined' && document.hidden) return false;
    // painel desligado (Configurações → Geral): nenhuma conversa está à vista
    if (!get().settings.showFriendsChat) return false;
    const ui = useFriendsUi.getState();
    const win = ui.windows.find((w) => w.userId === userId);
    if (!win || win.minimized) return false;
    // no celular a conversa só aparece com o painel aberto
    return !isMobileNow() || ui.open;
  }

  const typingTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /** quando o "digitando…" de cada pessoa da mesa some sozinho */
  const roomTypingTimers = new Map<string, ReturnType<typeof setTimeout>>();
  function setTyping(userId: string, typing: boolean): void {
    clearTimeout(typingTimers.get(userId));
    typingTimers.delete(userId);
    if (typing) {
      set((state) => ({ dmTyping: { ...state.dmTyping, [userId]: Date.now() + FRIENDS_LIMITS.typingTtlMs } }));
      // o aviso some sozinho se o "parou" não chegar
      typingTimers.set(userId, setTimeout(() => setTyping(userId, false), FRIENDS_LIMITS.typingTtlMs));
    } else if (get().dmTyping[userId] !== undefined) {
      set((state) => {
        const { [userId]: _gone, ...rest } = state.dmTyping;
        return { dmTyping: rest };
      });
    }
  }

  const roomName = (roomId: string): string | undefined =>
    get().openRooms.find((open) => open.roomId === roomId)?.name || get().rooms.find((card) => card.id === roomId)?.name;

  function forgetOpenRoom(roomId: string): void {
    background.get(roomId)?.close();
    background.delete(roomId);
    welcomes.delete(roomId);
    set((state) => ({ openRooms: withoutRoom(state.openRooms, roomId) }));
  }

  /**
   * A senha com que entrei em cada mesa nesta visita (só na memória, como a do `pendingJoin`). A mesa
   * que vai para o fundo leva a dela: se a conexão do fundo cair com a principal noutra mesa, voltar
   * pede a senha de novo, e sem ela a mesa saía das abertas.
   */
  const roomPasswords = new Map<string, string>();

  function startBackground(roomId: string, password: string | null = roomPasswords.get(roomId) ?? null): BackgroundRoom | null {
    const { token, user } = get();
    if (!token || !user) return null;
    const known = background.get(roomId);
    if (known) return known;
    const kept = new BackgroundRoom(
      roomId,
      token,
      user.id,
      {
        onReady: (_id, room) => set((state) => ({ openRooms: described(state.openRooms, room) })),
        onRenamed: (_id, room) => set((state) => ({ openRooms: described(state.openRooms, room) })),
        onClock: (id, session, live) => {
          if (background.get(id) !== kept) return;
          set((state) => ({ openRooms: withClock(state.openRooms, id, session, live) }));
        },
        onActivity: (id, item, context) => {
          if (background.get(id) !== kept) return;
          const state = get();
          const me = state.user;
          const message = item.message;
          const mention = !!message && !!me && (!!message.channel || mentions(message.text, [me.displayName, context.members[me.id]?.nick]));
          if (message) noticeChat(message, { user: me, members: context.members, room: context.room, settings: state.settings });
          set((now) => ({ openRooms: countUnread(now.openRooms, id, mention) }));
        },
        onRemoved: (id, reason, byName) => {
          if (reason !== 'deleted') noticeRoom(get().settings, 'kick', roomName(id) ?? 'RPG Desk');
          forgetOpenRoom(id);
          set({ removedNotice: { reason, byName } });
        },
        onFailed: (id, message) => {
          const name = roomName(id);
          forgetOpenRoom(id);
          set({ error: name ? `${name}: ${message}` : message });
        },
      },
      password,
    );
    background.set(roomId, kept);
    return kept;
  }

  function stopAllBackground(): void {
    for (const kept of background.values()) kept.close();
    background.clear();
    welcomes.clear();
  }

  /** Entra pela conexão principal (a da tela), como sempre foi. */
  function enterRoom(roomId: string, password: string | null): void {
    const token = get().token;
    if (!token) return;
    const switching = get().activeRoomId !== roomId;
    // o que ficou na fila durante uma queda era da mesa anterior: não sai nesta
    if (switching) socket.dropPending();
    if (password) roomPasswords.set(roomId, password);
    // a intenção fica guardada: o `hello` (inclusive após reconexão) a executa
    set((state) => ({
      // a conversa privada e a ficha pedidas na mesa anterior não abrem nesta (o layout da mesa
      // nova as aplicaria assim que carregasse)
      ...(switching ? { openChannel: null, openSheet: null, openModule: null } : {}),
      activeRoomId: roomId,
      pendingJoin: { roomId, password },
      error: null,
      room: null,
      // entrar de novo (não reconectar) põe as boas-vindas no fim outra vez; voltar a uma mesa aberta, não
      welcome: welcomes.get(roomId) ?? null,
      status: 'connecting',
      openRooms: markRead(withRoom(state.openRooms, roomId, roomName(roomId)), roomId),
    }));
    // conectado: entra já; senão o `hello` da conexão faz a entrada
    if (socket.isOpen()) socket.send({ type: 'room.join', roomId, password });
    else if (!socket.isActive()) socket.connect(token);
  }

  /**
   * Esquece a conta nesta aba (sair, conta apagada): a conexão, as mesas de fundo, a sessão guardada e
   * tudo o que era dela. A próxima conta nesta aba começa do zero.
   */
  /**
   * Entra no app depois do login, do cadastro ou da confirmação do e-mail: a conexão, as mesas e os
   * amigos (sem os amigos, o painel da conta nova ficava em "Carregando…").
   */
  async function enterApp(token: string): Promise<void> {
    socket.connect(token);
    void get().refreshContacts();
    await get().refreshRooms();
  }

  function forgetAccount(): void {
    socket.close();
    stopAllBackground();
    roomPasswords.clear();
    saveToken(null);
    releaseAllRolls();
    set({
      token: null,
      user: null,
      isAdmin: false,
      tosPending: false,
      emailConfirmation: null,
      rooms: [],
      activeRoomId: null,
      openRooms: [],
      // a próxima conta nesta aba não entra na mesa (nem com a senha) que esta pediu
      pendingJoin: null,
      room: null,
      users: [],
      messages: [],
      rolls: [],
      rollingDice: {},
      diceTextureRefused: null,
      audio: null,
      lastSfx: null,
      peerMedia: {},
      // amigos e conversas são desta conta
      contacts: null,
      userSearch: [],
      conversations: {},
      dmPeers: {},
      privacy: null,
      dmTyping: {},
      myStatus: null,
      profile: null,
      profileUserId: null,
      profileOpen: false,
      myProfile: null,
      // o plano e os arquivos são desta conta
      fileUsage: null,
      sessionFocus: null,
    });
    applyPlan(null);
    useFriendsUi.getState().reset();
    lastAway = null;
  }

  /** quando saiu o ping do `hello` (a ida e volta acerta o relógio do servidor) */
  let pingSentAt: number | null = null;

  /** uma busca de avisos por vez; o aviso que chega no meio pede outra no fim */
  let noticesBusy = false;
  let noticesAgain = false;

  /**
   * Busca os avisos de avaliação (docs/spec/session-clock.md, "Avaliações") e mostra cada um, com o
   * Ver que leva à sessão. Só a janela principal: buscar apaga, e as janelas próprias não mostrariam.
   */
  function takeSessionNotices(): void {
    const token = get().token;
    if (POPOUT || !token) return;
    if (noticesBusy) {
      noticesAgain = true;
      return;
    }
    noticesBusy = true;
    api
      .takeSessionNotices(token)
      .then(({ notices }) => {
        const language = useLanguage.getState().language;
        for (const notice of notices) {
          const where = { n: notice.number, room: notice.roomName };
          const text =
            notice.kind === 'gm'
              ? translate(language, 'sessions.notice.gm', { ...where, name: notice.byName ?? '' })
              : translate(language, notice.count > 1 ? 'sessions.notice.players.many' : 'sessions.notice.players.one', { ...where, count: notice.count });
          toast(text, 'info', { label: translate(language, 'sessions.notice.view'), run: () => get().focusGameSession(notice.roomId, notice.number) });
        }
      })
      .catch(() => {
        // sem rede, o pedido não chegou ao servidor: os avisos esperam a próxima conexão
      })
      .finally(() => {
        noticesBusy = false;
        if (noticesAgain) {
          noticesAgain = false;
          takeSessionNotices();
        }
      });
  }

  function apply(message: ServerMessage): void {
    switch (message.type) {
      case 'hello': {
        // o relógio do servidor (a música e os dados 3D comparam as horas dele); o pong acerta melhor
        noteServerTime(message.serverTime);
        pingSentAt = performance.now();
        socket.send({ type: 'ping' });
        const presence = !!message.features?.presence;
        // o meu status vem de lá (escolhido em outra janela, outra máquina ou antes de recarregar);
        // servidor sem status: Online, e a foto não abre o menu
        set({
          serverFeatures: {
            randomOrg: !!message.features?.randomOrg,
            presence,
            status: message.status !== undefined,
            imsAnyone: !!message.features?.imsAnyone,
            gameSessions: !!message.features?.gameSessions,
            sessionRatings: !!message.features?.sessionRatings,
            chatDelete: !!message.features?.chatDelete,
          },
          myStatus: message.status ?? 'online',
        });
        // os avisos de avaliação que chegaram com a conexão caída (ou com a conta fora)
        if (message.features?.sessionRatings) takeSessionNotices();
        // a conexão nova não sabe se esta janela está sem uso
        if (presence && lastAway !== null) socket.send({ type: 'presence.away', away: lastAway });
        hellos += 1;
        // reconectou: a presença dos amigos, as conversas abertas e os arquivos podem ter mudado no meio
        // (os avisos de arquivos, `files.changed` e `room.files`, que chegaram na queda se perderam)
        if (hellos > 1) {
          void get().refreshContacts();
          for (const win of useFriendsUi.getState().windows) void get().loadConversation(win.userId);
          set((state) => ({ filesVersion: state.filesVersion + 1 }));
          void get().refreshFiles();
        }
        // autenticado: agora entra na sala que a interface pediu (com a senha,
        // se houver). É também o que faz a reconexão voltar para a mesma sala.
        const pending = get().pendingJoin;
        if (pending) {
          socket.send({ type: 'room.join', roomId: pending.roomId, password: pending.password });
        } else {
          socket.flush();
        }
        return;
      }
      case 'ready':
        // a mesa que já não é a pedida (saiu dela, ou trocou por outra, enquanto a entrada chegava): sem
        // isto, ela voltava à tela, uma mesa fantasma. Saiu de vez: o servidor também a esquece
        if (get().activeRoomId !== message.state.room.id) {
          if (get().activeRoomId === null) socket.send({ type: 'room.leave' });
          return;
        }
        // o servidor manda o estado completo: reconectar == entrar de novo
        queueMicrotask(resubscribeAll);
        // de volta à sala: agora sai o que foi feito durante a queda
        queueMicrotask(() => socket.flush());
        // boas-vindas: depois do histórico que já estava lá quando entrei; reconectar não as move
        const lastBefore = message.history.reduce((at, m) => Math.max(at, m.createdAt), 0);
        const welcome = get().welcome?.roomId === message.state.room.id ? get().welcome! : { roomId: message.state.room.id, at: Math.max(message.serverTime, lastBefore + 1) };
        welcomes.set(welcome.roomId, welcome);
        // a mesa veio do fundo: a conexão principal já está nela, a de fundo sai sem ninguém notar
        background.get(message.state.room.id)?.close();
        background.delete(message.state.room.id);
        set((state) => ({
          openRooms: withClock(markRead(described(withRoom(state.openRooms, message.state.room.id), message.state.room), message.state.room.id), message.state.room.id, message.state.gameSession, false),
        }));
        set((state) => ({ readySeq: state.readySeq + 1 }));
        // as rolagens chegam de novo, com o histórico: nenhuma fica esperando os dados
        releaseAllRolls();
        set({
          welcome,
          room: message.state.room,
          users: message.state.users,
          members: Object.fromEntries(message.state.members.map((member) => [member.userId, member])),
          typing: {},
          audio: message.state.audio,
          peerMedia: Object.fromEntries(message.state.media.map((entry) => [entry.userId, entry.media])),
          messages: [...message.history, ...message.privateHistory].sort((x, y) => x.createdAt - y.createdAt),
          rolls: [...message.rollHistory].sort((x, y) => x.createdAt - y.createdAt),
          rollingDice: {},
          groups: Object.fromEntries(message.groups.map((group) => [group.id, group])),
          library: message.library,
          hiddenLibrary: [],
          plugins: localizePlugins(message.plugins, useLanguage.getState().language),
          pluginsRaw: message.plugins,
          status: 'online',
        });
        return;
      case 'presence.join':
        set((state) => ({
          users: state.users.some((user) => user.id === message.user.id) ? state.users : [...state.users, message.user],
          members: message.member ? { ...state.members, [message.user.id]: message.member } : state.members,
        }));
        return;
      case 'presence.leave':
        set((state) => {
          const { [message.userId]: _saiu, ...resto } = state.peerMedia;
          const { [message.userId]: _digitava, ...typing } = state.typing;
          // como no `ready`, os membros são de quem está na mesa (o combate põe os jogadores daqui)
          const { [message.userId]: _membro, ...members } = state.members;
          return {
            users: state.users.filter((user) => user.id !== message.userId),
            members,
            peerMedia: resto,
            typing,
          };
        });
        return;
      case 'presence.updated': {
        const before = get().members[message.member.userId];
        set((state) => ({ members: { ...state.members, [message.member.userId]: message.member } }));
        if (before) noticeBarChanges(before, message.member);
        return;
      }
      case 'presence.typing': {
        const who = message.userId;
        clearTimeout(roomTypingTimers.get(who));
        roomTypingTimers.delete(who);
        set((state) => {
          const { [who]: _antes, ...typing } = state.typing;
          return { typing: message.typing ? { ...typing, [who]: Date.now() } : typing };
        });
        // o "desligou" que não chega (quem digitava sumiu sem sair) não deixa o aviso preso: some
        // sozinho, na linha do chat e na lista de Participantes
        if (message.typing) {
          roomTypingTimers.set(
            who,
            setTimeout(() => {
              roomTypingTimers.delete(who);
              set((state) => {
                if (!(who in state.typing)) return state;
                const { [who]: _velho, ...typing } = state.typing;
                return { typing };
              });
            }, ROOM_TYPING_TTL_MS),
          );
        }
        return;
      }
      case 'room.updated':
        set((state) => ({ room: message.room, openRooms: described(state.openRooms, message.room) }));
        return;
      case 'library.item':
        set((state) => {
          const exists = state.library.some((i) => i.id === message.item.id);
          return {
            library: exists ? state.library.map((i) => (i.id === message.item.id ? message.item : i)) : [...state.library, message.item],
            hiddenLibrary: state.hiddenLibrary.filter((id) => id !== message.item.id),
          };
        });
        return;
      case 'library.removed':
        set((state) => ({ library: state.library.filter((i) => !message.itemIds.includes(i.id)), hiddenLibrary: state.hiddenLibrary.filter((id) => !message.itemIds.includes(id)) }));
        return;
      case 'library.hidden':
        set((state) => ({
          library: state.library.filter((i) => !message.itemIds.includes(i.id)),
          hiddenLibrary: [...state.hiddenLibrary.filter((id) => !message.itemIds.includes(id)), ...message.itemIds],
        }));
        return;
      case 'plugins.state':
        set({ plugins: localizePlugins(message.plugins, useLanguage.getState().language), pluginsRaw: message.plugins });
        return;
      case 'plugin.installResult': {
        const waiter = installWaiters.get(message.ref);
        installWaiters.delete(message.ref);
        waiter?.({ ok: message.ok, error: message.error === null ? null : serverText(message.error, useLanguage.getState().language) });
        return;
      }
      case 'plugin.webhook.result': {
        const waiter = webhookWaiters.get(message.ref);
        webhookWaiters.delete(message.ref);
        waiter?.({ ok: message.ok, status: message.status, body: message.body, error: message.error });
        return;
      }
      case 'plugin.webhooks':
        set((state) => ({ pluginWebhooks: { ...state.pluginWebhooks, [message.moduleId]: { hooks: message.hooks, log: message.log } } }));
        return;
      case 'ndb.snapshot':
      case 'ndb.batches':
      case 'ndb.applied':
      case 'ndb.reject':
        // passou do teto do documento no servidor: a mudança é desfeita, e a pessoa fica sabendo por quê
        if (message.type === 'ndb.reject' && message.error === TOO_LARGE) toast(translate(useLanguage.getState().language, 'library.tooLarge'), 'error');
        handleNdbMessage(message);
        return;
      case 'group.state':
        set((state) => ({
          groups: { ...state.groups, [message.group.id]: message.group },
          // grupo novo para mim: abre a aba
          openChannel: state.groups[message.group.id]
            ? state.openChannel
            : { channel: { kind: 'group', groupId: message.group.id }, focus: message.group.createdBy === state.user?.id, nonce: (state.openChannel?.nonce ?? 0) + 1 },
        }));
        return;
      case 'group.left':
        set((state) => {
          const { [message.groupId]: _saiu, ...groups } = state.groups;
          return { groups };
        });
        return;
      case 'room.removed':
        if (message.reason !== 'deleted') noticeRoom(get().settings, 'kick', get().room?.name ?? 'RPG Desk');
        get().leaveRoom();
        set({ removedNotice: { reason: message.reason, byName: message.byName } });
        return;
      case 'chat.removed': {
        // as linhas de uma sessão apagada (docs/spec/session-clock.md, "Apagar uma sessão") e as que um
        // mestre apagou ou limpou, com as rolagens delas (docs/spec/chat-delete.md)
        const gone = new Set([...message.ids, ...(message.rollIds ?? [])]);
        // a rolagem apagada com os dados ainda rolando: a ficha que esperava recebe o resultado
        for (const id of gone) get().revealRoll(id);
        set((state) => ({
          ...(state.messages.some((existing) => gone.has(existing.id)) ? { messages: state.messages.filter((existing) => !gone.has(existing.id)) } : {}),
          ...(state.rolls.some((existing) => gone.has(existing.id)) ? { rolls: state.rolls.filter((existing) => !gone.has(existing.id)) } : {}),
          chatRemovedVersion: { ...state.chatRemovedVersion, [message.roomId]: (state.chatRemovedVersion[message.roomId] ?? 0) + 1 },
        }));
        return;
      }
      case 'chat.message':
        noticeChat(message.message, get());
        set((state) => {
          if (state.messages.some((existing) => existing.id === message.message.id)) return state;
          // conversa privada recebida: a área de trabalho abre a aba (sem roubar o foco)
          const incoming = message.message.channel;
          const me = state.user?.id;
          const openChannel =
            incoming && message.message.userId !== me && message.message.kind !== 'system'
              ? {
                  channel: incoming.kind === 'pvt' ? ({ kind: 'pvt', to: message.message.userId } as ChatChannel) : incoming,
                  focus: false,
                  nonce: (state.openChannel?.nonce ?? 0) + 1,
                }
              : state.openChannel;
          // quem falou parou de digitar
          const { [message.message.userId]: _parou, ...typing } = state.typing;
          return { messages: [...state.messages, message.message].slice(-MAX_MESSAGES), typing, openChannel };
        });
        return;
      case 'dice.result': {
        const { roll } = message;
        const waiter = roll.ref ? rollWaiters.get(roll.ref) : undefined;
        if (roll.ref) rollWaiters.delete(roll.ref);
        // com os dados 3D rolando nesta tela, o resultado (no chat e para a ficha que rolou) espera os
        // dados pararem (docs/spec/dice-3d.md, "Resultado depois da animação"). A janela própria não tem
        // dados: espera o aviso da principal, a não ser que ele já tenha chegado
        const { settings, room } = get();
        const holds = !shownByMain.delete(roll.id) && !held.has(roll.id) && animatesRoll(roll, settings, room);
        if (holds) held.set(roll.id, { roomId: roll.roomId, timer: setTimeout(() => get().revealRoll(roll.id), REVEAL_CAP_MS), release: waiter ? () => waiter(roll) : null });
        set((state) => ({
          rolls: [...state.rolls, roll].slice(-MAX_ROLLS),
          ...(holds ? { rollingDice: { ...state.rollingDice, [roll.id]: true as const } } : {}),
          ...textureEcho(state, roll),
        }));
        if (!holds) {
          waiter?.(roll);
          rollShown(roll.roomId, roll.id);
        }
        // com os dados 3D, o som é o das batidas (dice/DiceLayer); sem eles, o aviso curto
        if (!diceOn(settings, room)) noticeRoom(settings, 'roll', room?.name ?? 'RPG Desk');
        return;
      }
      case 'audio.state':
        set({ audio: message.audio });
        return;
      case 'gameSession.state':
        set((state) => ({
          openRooms: withClock(state.openRooms, message.roomId, message.session, true),
          gameSessionsVersion: { ...state.gameSessionsVersion, [message.roomId]: (state.gameSessionsVersion[message.roomId] ?? 0) + 1 },
        }));
        return;
      case 'gameSession.history':
        set((state) => ({ gameSessionsVersion: { ...state.gameSessionsVersion, [message.roomId]: (state.gameSessionsVersion[message.roomId] ?? 0) + 1 } }));
        return;
      case 'gameSession.notices':
        takeSessionNotices();
        return;
      case 'audio.sfx':
        set((state) => ({ lastSfx: message.event, sfxNonce: state.sfxNonce + 1 }));
        return;
      case 'scene.ping':
        for (const listener of pingListeners) listener({ itemId: message.itemId, userId: message.userId, x: message.x, y: message.y, focus: message.focus });
        return;
      case 'scene.door.locked':
        for (const listener of doorListeners) listener({ itemId: message.itemId, wallId: message.wallId, userId: message.userId });
        return;
      case 'scene.invisible.notice':
        for (const listener of invisibleListeners) listener({ itemId: message.itemId, tokenId: message.tokenId, userId: message.userId, change: message.change });
        return;
      case 'rtc.signal':
        // cada sinal vai na ordem para quem escuta (vários chegam de uma vez)
        for (const listener of signalListeners) listener(message.fromUserId, message.signal);
        return;
      case 'rtc.media':
        set((state) => {
          if (message.media.joined) return { peerMedia: { ...state.peerMedia, [message.userId]: message.media } };
          const { [message.userId]: _saiu, ...rest } = state.peerMedia;
          return { peerMedia: rest };
        });
        return;
      case 'ims.message': {
        const dm = message.message;
        const { user: me, settings } = get();
        const mine = dm.fromId === me?.id;
        const otherId = mine ? dm.toId : dm.fromId;
        // a mesma mensagem chega pelo POST e pelo aviso às minhas outras janelas
        if (get().conversations[otherId]?.messages.some((m) => m.id === dm.id)) return;
        addMessages(otherId, [dm]);
        // quem não é amigo fica nas "Outras conversas" (quem mandou vem junto; na minha, a janela sabe)
        touchOther(otherId, dm, mine ? get().dmPeers[otherId]?.user : message.from);
        if (mine) return;
        setTyping(otherId, false);
        const contact = friendOf(otherId);
        const other = get().contacts?.others.find((c) => c.user.id === otherId);
        if (conversationVisible(otherId)) {
          // à vista: lida no servidor também. O contador daqui nem subiu, então markConversationRead
          // acharia que não há nada a marcar e não iria ao servidor
          const token = get().token;
          if (token) void api.markDirectMessagesRead(token, otherId).catch(() => undefined);
          return;
        }
        patchUnread(otherId, (n) => n + 1);
        // Ocupado: a mensagem fica só como não lida, sem som, sem aviso e sem abrir a conversa
        if (get().myStatus === 'busy') return;
        const from = contact?.user.displayName ?? other?.user.displayName ?? message.from?.displayName ?? '…';
        const text = dm.invite ? translate(useLanguage.getState().language, 'friends.invite.notice', { room: dm.invite.roomName }) : dm.text;
        noticeMessage(settings, from, text, (line) => toast(line), from);
        // como na Steam: a conversa aparece minimizada embaixo, se houver vaga (no celular, só o número;
        // com o painel desligado em Configurações → Geral, nada abre)
        const ui = useFriendsUi.getState();
        if (!POPOUT && !isMobileNow() && settings.showFriendsChat && !ui.windows.some((w) => w.userId === otherId) && hasRoomForWindow(ui.windows, maxWindows(window.innerWidth))) {
          ui.openWindow(otherId, { minimized: true });
          void get().loadConversation(otherId);
        }
        return;
      }
      case 'presence.status':
        // escolhido nesta janela (a confirmação) ou em outra
        set({ myStatus: message.status });
        return;
      case 'ims.typing':
        setTyping(message.userId, message.typing);
        return;
      case 'ims.read':
        // li em outra janela
        patchUnread(message.userId, () => 0);
        return;
      case 'ims.dismissed':
        // tirei da lista em outra janela
        set((state) => (state.contacts ? { contacts: { ...state.contacts, others: state.contacts.others.filter((c) => c.user.id !== message.userId) } } : state));
        useFriendsUi.getState().closeWindow(message.userId);
        return;
      case 'contact.presence': {
        const presence = message.presence ?? { ...OFFLINE_PRESENCE, online: message.online };
        if (!presence.online) setTyping(message.userId, false);
        patchContact(message.userId, (c) => ({
          ...c,
          online: presence.online,
          presence,
          ...(message.lastSeenAt !== undefined ? { lastSeenAt: message.lastSeenAt } : {}),
        }));
        return;
      }
      case 'contact.updated':
      case 'contact.removed':
        // pedido recebido, aceito, bloqueado…: a lista é a fonte da verdade
        void get().refreshContacts();
        return;
      case 'user.updated': {
        const updated = message.user;
        set((state) => ({
          user: state.user?.id === updated.id ? updated : state.user,
          users: state.users.map((u) => (u.id === updated.id ? updated : u)),
          contacts: state.contacts
            ? {
                ...state.contacts,
                friends: state.contacts.friends.map((c) => (c.user.id === updated.id ? { ...c, user: updated } : c)),
                hidden: state.contacts.hidden.map((c) => (c.user.id === updated.id ? { ...c, user: updated } : c)),
                others: state.contacts.others.map((c) => (c.user.id === updated.id ? { ...c, user: updated } : c)),
              }
            : state.contacts,
          dmPeers: state.dmPeers[updated.id] ? { ...state.dmPeers, [updated.id]: { ...state.dmPeers[updated.id]!, user: updated } } : state.dmPeers,
          profile: state.profile?.user.id === updated.id ? { ...state.profile, user: updated } : state.profile,
          myProfile: state.myProfile?.user.id === updated.id ? { ...state.myProfile, user: updated } : state.myProfile,
        }));
        return;
      }
      case 'session.ended':
        // a sessão já não vale no servidor: sair (sem tentar reconectar) e dizer o porquê na entrada
        void get().logout();
        set({ signedOutNotice: { reason: message.reason, detail: message.detail ?? '' } });
        return;
      case 'account.admin':
        set({ isAdmin: message.admin });
        return;
      case 'account.plan':
        applyPlan(message.plan);
        return;
      case 'plans.updated':
        set({ planCatalog: message.plans });
        return;
      case 'files.changed':
        // noutra aba, noutro aparelho ou um envio daqui: o espaço e quem mostra arquivos releem
        set((state) => ({ filesVersion: state.filesVersion + 1 }));
        void get().refreshFiles();
        return;
      case 'room.files':
        set((state) => ({ roomFilesVersion: { ...state.roomFilesVersion, [message.roomId]: (state.roomFilesVersion[message.roomId] ?? 0) + 1 } }));
        return;
      case 'profile.updated':
        // se é o perfil que estou olhando, atualiza na hora
        set((state) => (state.profileUserId === message.profile.user.id ? { profile: message.profile } : state));
        return;
      case 'error':
        // falha ao ENTRAR (senha errada, sala sumiu) devolve o usuário à lista;
        // erro dentro da sala (permissão de mapa, por exemplo) é só um aviso
        if (get().room === null && get().pendingJoin !== null) {
          const refused = get().pendingJoin!.roomId;
          welcomes.delete(refused);
          roomPasswords.delete(refused);
          set((state) => ({ error: serverText(message.message, useLanguage.getState().language), activeRoomId: null, pendingJoin: null, openRooms: withoutRoom(state.openRooms, refused) }));
        } else {
          set({ error: serverText(message.message, useLanguage.getState().language) });
        }
        return;
      case 'pong':
        if (pingSentAt !== null) noteServerTime(message.serverTime, performance.now() - pingSentAt);
        pingSentAt = null;
        return;
    }
  }

  // a sessão que o servidor recusou (saiu noutra aba, a senha mudou noutro aparelho): sai da conta, em
  // vez de seguir reconectando com ela para sempre
  setSessionRefusedHandler(() => {
    if (!get().token) return;
    void get().logout().then(() => set({ error: serverText('faça login para continuar', useLanguage.getState().language) }));
  });
  let checkingSession = false;
  const socket = new RpgDeskSocket({
    onMessage: apply,
    onStatus: (status) => set({ status }),
    onProblem: (problem) => {
      reportProblem(problem);
      set({ error: problem });
    },
    // a conexão foi recusada: o /api/me diz se é a sessão (401, que sai pelo aviso acima) ou a rede
    onRefused: () => {
      const token = get().token;
      if (!token || checkingSession) return;
      checkingSession = true;
      api
        .me(token)
        .catch(() => undefined)
        .finally(() => {
          checkingSession = false;
        });
    },
  });
  setNdbSender((message) => socket.send(message));
  // as mesmas regras do servidor (apps/server/src/library.ts): mestre é dono; o grupo vem do papel
  setNdbIdentity(() => {
    const { user, room, members } = get();
    if (!user || !room) return null;
    const role = room.ownerId === user.id ? 'gm' : roleOfModes(members[user.id]?.modes ?? []);
    const gm = role === 'gm';
    const group = gm ? NDB_GROUPS.gms : role === 'player' ? NDB_GROUPS.players : NDB_GROUPS.spectators;
    return { who: { login: user.id, groups: [NDB_GROUPS.everyone, group] }, owner: gm };
  });

  return {
    token: loadToken(),
    user: null,
    isAdmin: false,
    tosPending: false,
    emailConfirmation: null,
    emailLinkNotice: null,
    dismissEmailLinkNotice: () => set({ emailLinkNotice: null }),
    plan: null,
    planCeilings: {},
    planCatalog: null,
    signedOutNotice: null,
    dismissSignedOutNotice: () => set({ signedOutNotice: null }),
    rooms: [],
    activeRoomId: null,
    openRooms: [],
    pendingJoin: null,
    serverFeatures: { randomOrg: false, presence: false, status: false, imsAnyone: false, gameSessions: false, sessionRatings: false, chatDelete: false },
    room: null,
    readySeq: 0,
    users: [],
    members: {},
    typing: {},
    groups: {},
    openChannel: null,
    library: [],
    hiddenLibrary: [],
    plugins: [],
    pluginsRaw: [],
    pluginWebhooks: {},
    openSheet: null,
    openModule: null,
    chatRun: null,
    welcome: null,
    removedNotice: null,
    messages: [],
    rolls: [],
    rollingDice: {},
    diceTextureRefused: null,
    audio: null,
    lastSfx: null,
    sfxNonce: 0,
    peerMedia: {},
    contacts: null,
    userSearch: [],
    conversations: {},
    dmPeers: {},
    privacy: null,
    dmTyping: {},
    myStatus: null,
    profile: null,
    profileOpen: false,
    profileUserId: null,
    myProfile: null,
    fileUsage: null,
    filesVersion: 0,
    pendingFileUse: null,
    roomFilesVersion: {},
    gameSessionsVersion: {},
    chatRemovedVersion: {},
    sessionFocus: null,
    settings: settingsIniciais,
    status: 'offline',
    busy: false,
    error: null,

    async initialize() {
      const token = get().token;
      if (!token) return;
      try {
        const { user, admin, plan, ceilings, plans, tosPending, emailConfirmation } = await api.me(token);
        // entrou com outra conta (ou saiu) enquanto a sessão guardada era conferida: vale a de agora
        if (get().token !== token) return;
        set({ user, isAdmin: admin, planCeilings: ceilings, planCatalog: plans, tosPending, emailConfirmation });
        applyPlan(plan);
        // a conta que espera a confirmação do e-mail fica na tela de espera: o servidor recusaria o resto
        if (emailConfirmation) return;
        socket.connect(token);
        await get().refreshRooms();
        await get().refreshContacts();
      } catch (error) {
        if (get().token !== token) return;
        // só a sessão recusada (401) sai: sem rede ou com o servidor em pane, a sessão guardada fica
        // (abrir de novo entra direto) e a tela diz o que houve
        if (error instanceof ApiError && error.status === 401) {
          saveToken(null);
          set({ token: null, user: null });
          return;
        }
        set({ error: describeError(error) });
      }
    },

    async register(input, keepSignedIn = true) {
      set({ busy: true, error: null });
      try {
        const { token, user, admin, plan, ceilings, plans, tosPending, emailConfirmation } = await api.register({ ...input, language: useLanguage.getState().language });
        saveToken(token, keepSignedIn);
        set({ token, user, isAdmin: admin, planCeilings: ceilings, planCatalog: plans, tosPending, emailConfirmation, signedOutNotice: null });
        applyPlan(plan);
        if (!emailConfirmation) await enterApp(token);
        return true;
      } catch (error) {
        set({ error: describeError(error) });
        return false;
      } finally {
        set({ busy: false });
      }
    },

    async login(account, password, keepSignedIn = true) {
      set({ busy: true, error: null });
      try {
        const { token, user, admin, plan, ceilings, plans, tosPending, emailConfirmation } = await api.login(account, password);
        saveToken(token, keepSignedIn);
        set({ token, user, isAdmin: admin, planCeilings: ceilings, planCatalog: plans, tosPending, emailConfirmation, signedOutNotice: null });
        applyPlan(plan);
        if (!emailConfirmation) await enterApp(token);
        return true;
      } catch (error) {
        set({ error: describeError(error) });
        return false;
      } finally {
        set({ busy: false });
      }
    },

    async logout() {
      const token = get().token;
      forgetAccount();
      if (token) {
        try {
          await api.logout(token);
        } catch {
          // sair localmente já basta; o token expira no servidor de qualquer forma
        }
      }
    },

    async acceptTos() {
      const token = get().token;
      if (!token) return null;
      try {
        await api.acceptTos(token, CURRENT_TOS_VERSION);
        set({ tosPending: false });
        return null;
      } catch (error) {
        return describeError(error);
      }
    },

    async refreshEmailConfirmation() {
      const token = get().token;
      if (!token || !get().emailConfirmation) return;
      try {
        const { emailConfirmation } = await api.me(token);
        if (get().token !== token || !get().emailConfirmation) return;
        set({ emailConfirmation });
        if (!emailConfirmation) await enterApp(token);
      } catch {
        // sem rede, a próxima volta tenta de novo; a sessão recusada (401) já sai pelo api.ts
      }
    },

    async resendConfirmation() {
      const token = get().token;
      if (!token) return null;
      try {
        const { emailConfirmation } = await api.resendConfirmation(token, useLanguage.getState().language);
        set({ emailConfirmation });
        return null;
      } catch (error) {
        // a espera mudou (o limite da hora, ou a confirmação chegou noutra aba): relê
        void get().refreshEmailConfirmation();
        return describeError(error);
      }
    },

    async changePendingEmail(email) {
      const token = get().token;
      if (!token) return null;
      try {
        const { emailConfirmation } = await api.changePendingEmail(token, email, useLanguage.getState().language);
        set({ emailConfirmation });
        return null;
      } catch (error) {
        void get().refreshEmailConfirmation();
        return describeError(error);
      }
    },

    async confirmEmailLink(code) {
      try {
        await api.confirmEmail(code);
        set({ emailLinkNotice: 'confirmed' });
      } catch (error) {
        const status = error instanceof ApiError ? error.status : 0;
        set({ emailLinkNotice: status === 410 ? 'expired' : status === 404 || status === 400 ? 'invalid' : 'failed' });
      }
    },

    async refreshRooms() {
      const token = get().token;
      if (!token) return;
      try {
        const { rooms } = await api.listRooms(token);
        set({ rooms });
      } catch (error) {
        set({ error: describeError(error) });
      }
    },

    async createRoom(input) {
      const token = get().token;
      if (!token) return;
      set({ busy: true, error: null });
      try {
        const { room } = await api.createRoom(token, input);
        set((state) => ({ rooms: [room, ...state.rooms.filter((existing) => existing.id !== room.id)] }));
        // o dono entra sem senha (o servidor permite)
        get().joinRoom(room.id);
      } catch (error) {
        set({ error: describeError(error) });
      } finally {
        set({ busy: false });
      }
    },

    joinRoom(roomId, password = null) {
      if (!get().token) return;
      const previous = get().activeRoomId;
      const seq = ++switchSeq;
      // janela destacada, nenhuma mesa na tela ou a mesma mesa: entra (ou reentra) como sempre
      if (POPOUT || !previous || previous === roomId) {
        enterRoom(roomId, password);
        return;
      }
      const open = get().openRooms;
      if (!open.some((room) => room.roomId === roomId) && open.length >= MAX_OPEN_ROOMS) {
        set({ error: translate(useLanguage.getState().language, 'logged.rooms.tooMany', { max: MAX_OPEN_ROOMS }) });
        return;
      }
      // a mesa da tela vai para o fundo: a voz termina nela, e uma conexão própria a mantém aberta
      // (entra antes de a principal sair, então ninguém vê saída nem entrada)
      const me = get().user?.id;
      if (me && get().peerMedia[me]?.joined) get().sendMedia(EMPTY_MEDIA);
      const kept = startBackground(previous);
      const wait = kept ? Promise.race([kept.ready, new Promise<boolean>((resolve) => window.setTimeout(() => resolve(false), 5000))]) : Promise.resolve(false);
      void wait.then(() => {
        if (seq !== switchSeq) return;
        resetDocs();
        enterRoom(roomId, password);
      });
    },

    leaveOpenRoom(roomId) {
      if (roomId === get().activeRoomId) get().leaveRoom();
      else forgetOpenRoom(roomId);
    },

    reopenRooms(roomIds, activeId) {
      set((state) => ({ openRooms: roomIds.reduce((list, id) => withRoom(list, id, roomName(id)), state.openRooms) }));
      for (const id of roomIds) if (id !== activeId) startBackground(id);
      if (activeId) get().joinRoom(activeId);
    },

    async toggleFavorite(roomId) {
      const token = get().token;
      const card = get().rooms.find((candidate) => candidate.id === roomId);
      if (!token || !card) return;
      try {
        const { room } = await api.setFavorite(token, roomId, !card.isFavorite);
        set((state) => ({ rooms: state.rooms.map((item) => (item.id === room.id ? room : item)) }));
      } catch (error) {
        set({ error: describeError(error) });
      }
    },

    async voteRoom(roomId, value) {
      const token = get().token;
      if (!token) return;
      try {
        // clicar no mesmo voto remove (o servidor trata 0 como "sem voto")
        const { room } = await api.voteRoom(token, roomId, value);
        set((state) => ({ rooms: state.rooms.map((item) => (item.id === room.id ? room : item)) }));
      } catch (error) {
        set({ error: describeError(error) });
      }
    },

    rollForPlugin(expression, label) {
      const tr = (key: string, params?: Record<string, string | number>) => translate(useLanguage.getState().language, key, params);
      // uma rolagem inválida volta na hora, com a mensagem na língua de quem rolou (o servidor
      // recusaria sem dizer a qual pedido, e a promessa esperaria o prazo inteiro)
      const problem = rollProblem(expression, tr);
      if (problem) return Promise.reject(new Error(problem));
      const ref = `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
      return new Promise<RollResult>((resolve, reject) => {
        const timer = setTimeout(() => {
          rollWaiters.delete(ref);
          reject(new Error(tr('dice.error.timeout')));
        }, 30_000);
        rollWaiters.set(ref, (roll) => {
          clearTimeout(timer);
          resolve(roll);
        });
        // o protocolo aceita até 80 caracteres no rótulo: acima, o servidor recusaria a rolagem inteira
        socket.send({ type: 'dice.roll', expression, label: label === null ? null : label.slice(0, 80), ref, ...diceStyle(get().settings), ...diceEffects(get().settings) });
      });
    },
    revealRoll(rollId) {
      const hold = held.get(rollId);
      if (!hold) return;
      held.delete(rollId);
      clearTimeout(hold.timer);
      set((state) => {
        const { [rollId]: _solta, ...rollingDice } = state.rollingDice;
        return { rollingDice };
      });
      hold.release?.();
      rollShown(hold.roomId, rollId);
    },
    installWebPlugin(fileId) {
      return awaitInstall((ref) => socket.send({ type: 'plugin.installWeb', fileId, ref }));
    },
    installOfficialPlugin(officialId) {
      return awaitInstall((ref) => socket.send({ type: 'plugin.installOfficial', officialId, ref }));
    },
    sendWebhook(moduleId, name, data) {
      const ref = newRef('w');
      return new Promise((resolve) => {
        const timer = setTimeout(() => {
          webhookWaiters.delete(ref);
          resolve({ ok: false, status: 0, body: null, error: 'timeout' });
        }, 20_000);
        webhookWaiters.set(ref, (result) => {
          clearTimeout(timer);
          resolve(result);
        });
        socket.send({ type: 'plugin.webhook.send', ref, moduleId, name, data });
      });
    },
    loadWebhooks(moduleId) {
      socket.send({ type: 'plugin.webhooks.get', moduleId });
    },
    setWebhook(moduleId, name, url, who) {
      socket.send({ type: 'plugin.webhook.set', moduleId, name, url, who });
    },
    addLocalLine(text) {
      const me = get().user;
      const room = get().room;
      if (!me || !room) return;
      const line: ChatMessage = { id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, roomId: room.id, userId: me.id, displayName: me.displayName, color: me.color, kind: 'system', text, createdAt: Date.now() };
      set((state) => ({ messages: [...state.messages, line].slice(-MAX_MESSAGES) }));
    },
    runInChat(text) {
      const roomId = get().room?.id;
      if (!roomId) return;
      const id = newChatRunId();
      set((state) => ({ chatRun: { id, roomId, text, nonce: (state.chatRun?.nonce ?? 0) + 1, at: Date.now(), done: false } }));
      // sem chat principal nesta janela (ele está numa janela destacada, ou esta é a destacada do
      // painel): vai também para as outras janelas da mesa, e a que rodar avisa
      if (!mainChatHere()) postWindows({ t: 'run-chat', roomId, runId: id, text });
    },
    takeChatRun(nonce) {
      const run = get().chatRun;
      if (!run || run.nonce !== nonce || run.done) return false;
      set({ chatRun: { ...run, done: true } });
      return true;
    },
    requestOpenSheet(itemId, opts = {}) {
      // numa janela própria, quem abre é a mesa (a janela principal), nesta janela; a janela nova, se
      // pedida, abre daqui mesmo (o navegador só deixa quem recebeu o clique abrir)
      if (POPOUT) {
        let popWindow: string | undefined;
        if (opts.window) {
          popWindow = newLayoutId('w');
          if (!openPopout(popWindow, POPOUT.roomId)) {
            toast(translate(useLanguage.getState().language, 'room.popout.blocked'), 'error');
            return;
          }
        }
        postWindows({ t: 'open-item', roomId: POPOUT.roomId, itemId, windowId: POPOUT.windowId, ...(popWindow ? { popWindow } : {}) });
        return;
      }
      set((state) => ({
        openSheet: {
          itemId,
          ...(opts.window || opts.popWindow ? { window: true } : {}),
          ...(opts.popWindow ? { popWindow: opts.popWindow } : {}),
          ...(opts.within ? { within: opts.within } : {}),
          nonce: (state.openSheet?.nonce ?? 0) + 1,
        },
      }));
    },
    requestOpenModule(classId) {
      set((state) => ({ openModule: { classId, nonce: (state.openModule?.nonce ?? 0) + 1 } }));
    },
    focusGameSession(roomId, number) {
      set((state) => ({ sessionFocus: { roomId, number, nonce: (state.sessionFocus?.nonce ?? 0) + 1, opened: false } }));
      if (get().activeRoomId !== roomId) get().joinRoom(roomId);
    },
    settleSessionFocus(nonce, stage) {
      const focus = get().sessionFocus;
      if (focus?.nonce !== nonce) return;
      set({ sessionFocus: stage === 'opened' ? { ...focus, opened: true } : null });
    },
    sendRaw(message) {
      socket.send(message);
    },
    createLibraryItem(input) {
      socket.send({
        type: 'library.create',
        kind: input.kind,
        name: input.name,
        parentId: input.parentId ?? null,
        dataType: input.dataType ?? null,
        ...(input.ownerId !== undefined ? { ownerId: input.ownerId } : {}),
      });
    },
    renameLibraryItem(itemId, name) {
      socket.send({ type: 'library.rename', itemId, name });
    },
    moveLibraryItem(itemId, parentId) {
      socket.send({ type: 'library.move', itemId, parentId });
    },
    setDockActive(itemId, active) {
      socket.send({ type: 'library.dockActive', itemId, active });
    },
    deleteLibraryItem(itemId) {
      socket.send({ type: 'library.delete', itemId });
    },
    setLibraryOwner(itemId, ownerId) {
      socket.send({ type: 'library.setOwner', itemId, ownerId });
    },
    setLibraryVisible(itemId, visible) {
      socket.send({ type: 'library.setVisible', itemId, visible });
    },
    leaveRoom(farewell) {
      resetDocs();
      switchSeq += 1;
      const leaving = get().activeRoomId;
      if (leaving) {
        welcomes.delete(leaving);
        set((state) => ({ openRooms: withoutRoom(state.openRooms, leaving) }));
      }
      // a conexão continua (contatos e mensagens diretas); só sai da sala. Sem conexão, o servidor já
      // tirou a pessoa da mesa: guardado na fila, o pedido a tiraria da próxima mesa em que entrasse
      socket.dropPending();
      if (socket.isOpen()) socket.send({ type: 'room.leave', ...(farewell?.trim() ? { message: farewell.trim().slice(0, 200) } : {}) });
      releaseAllRolls();
      set({
        activeRoomId: null,
        pendingJoin: null,
        room: null,
        welcome: null,
        users: [],
        members: {},
        typing: {},
        groups: {},
        openChannel: null,
        library: [],
        hiddenLibrary: [],
        plugins: [],
        pluginsRaw: [],
        pluginWebhooks: {},
        messages: [],
        rolls: [],
        rollingDice: {},
        audio: null,
        lastSfx: null,
        sfxNonce: 0,
            peerMedia: {},
      });
    },

    setRole(userId, role) {
      socket.send({ type: 'presence.setRole', userId, role });
    },
    setFlag(userId, flag, on) {
      socket.send({ type: 'presence.setFlag', userId, flag, on });
    },
    kick(userId, ban) {
      socket.send({ type: 'presence.kick', userId, ban });
    },
    setNick(nick) {
      socket.send({ type: 'presence.nick', nick: nick?.trim() ? nick.trim().slice(0, 40) : null });
    },
    setMainCharacter(itemId, userId) {
      socket.send({ type: 'presence.mainCharacter', itemId, ...(userId ? { userId } : {}) });
    },
    setCardBar(userId, index, value, max, color) {
      const { user, members } = get();
      const current = members[userId]?.bars[index] ?? null;
      // o plugin repete o mesmo valor a cada mudança da ficha: só manda o que mudou
      if (current ? current.value === value && current.max === max && (!color || current.color === color) : max <= 0) return;
      socket.send({ type: 'presence.bar', ...(userId !== user?.id ? { userId } : {}), index, value, max, ...(color ? { color } : {}) });
    },
    setCardLine(userId, index, text) {
      const { user, members } = get();
      if ((members[userId]?.lines[index] ?? '') === text.trim()) return;
      socket.send({ type: 'presence.line', ...(userId !== user?.id ? { userId } : {}), index, text: text.trim().slice(0, 120) });
    },
    setModerated(on) {
      socket.send({ type: 'room.moderate', on });
    },
    sendTyping(typing) {
      socket.send({ type: 'chat.typing', typing });
    },
    dismissRemovedNotice() {
      set({ removedNotice: null });
    },
    sendChat(text, kind = 'chat', persona, channel) {
      const trimmed = text.trim();
      if (!trimmed) return;
      socket.send({ type: 'chat.send', text: trimmed, kind, ...(persona ? { persona } : {}), ...(channel ? { channel } : {}) });
    },

    rollDice(expression, label = null, channel) {
      socket.send({ type: 'dice.roll', expression, label, ...(channel ? { channel } : {}), ...diceStyle(get().settings), ...diceEffects(get().settings) });
    },
    attachStoredFile(fileId, channel, caption = '') {
      socket.send({ type: 'chat.attach', fileId, caption, ...(channel ? { channel } : {}) });
    },
    setPendingFileUse(use) {
      set({ pendingFileUse: use });
    },
    takePendingFileUse(nonce) {
      if (get().pendingFileUse?.nonce !== nonce) return false;
      set({ pendingFileUse: null });
      return true;
    },
    async attachFile(file, channel, caption = '') {
      const token = get().token;
      if (!token) return false;
      // avisa antes de enviar o que o servidor recusaria (docs/spec/planos.md)
      const refusal = uploadRefusal(get(), file.size, say);
      if (refusal) {
        set({ error: refusal });
        return false;
      }
      const roomId = get().activeRoomId;
      try {
        const stored = await sendToCloud(file, { roomId });
        // trocou de mesa durante o envio: o anexo não vai para o chat da mesa nova (o arquivo fica no
        // Desk Cloud, ligado à mesa em que foi enviado)
        if (get().activeRoomId !== roomId || get().room?.id !== roomId) return false;
        socket.send({ type: 'chat.attach', fileId: stored.id, caption, ...(channel ? { channel } : {}) });
        return true;
      } catch (error) {
        set({ error: describeError(error) });
        return false;
      }
    },
    openPrivate(channel, focus = true, within) {
      if (POPOUT) {
        postWindows({ t: 'open-channel', roomId: POPOUT.roomId, channel, windowId: POPOUT.windowId });
        return;
      }
      set((state) => ({ openChannel: { channel, focus, ...(within ? { within } : {}), nonce: (state.openChannel?.nonce ?? 0) + 1 } }));
    },
    createGroup(userIds) {
      socket.send({ type: 'group.create', userIds });
    },
    inviteToGroup(groupId, userIds) {
      socket.send({ type: 'group.invite', groupId, userIds });
    },
    partGroup(groupId) {
      socket.send({ type: 'group.part', groupId });
    },

    enqueueAudio(url, title, kind = 'music') {
      const cleanUrl = url.trim();
      const cleanTitle = title.trim();
      if (!cleanUrl || !cleanTitle) return;
      socket.send({ type: 'audio.enqueue', url: cleanUrl, title: cleanTitle, kind });
    },

    controlAudio(action, extra = {}) {
      socket.send({
        type: 'audio.control',
        action,
        ...(extra.positionSeconds === undefined ? {} : { positionSeconds: extra.positionSeconds }),
        ...(extra.trackId === undefined ? {} : { trackId: extra.trackId }),
      });
    },

    controlGameSession(action) {
      const { activeRoomId, openRooms } = get();
      if (!activeRoomId || !socket.isOpen()) return false;
      const session = openRooms.find((open) => open.roomId === activeRoomId)?.clock?.session ?? null;
      const number = action === 'start' ? nextGameSessionNumber(session) : session?.number;
      if (!number) return false;
      socket.send({ type: 'gameSession.control', action, number });
      return true;
    },

    deleteChatLine(id) {
      if (!get().activeRoomId || !socket.isOpen()) return false;
      socket.send({ type: 'chat.delete', id });
      return true;
    },

    clearChat() {
      if (!get().activeRoomId || !socket.isOpen()) return false;
      socket.send({ type: 'chat.clear' });
      return true;
    },

    removeAudio(trackId) {
      socket.send({ type: 'audio.remove', trackId });
    },

    clearAudio() {
      socket.send({ type: 'audio.clear' });
    },

    setAudioVolume(volume) {
      socket.send({ type: 'audio.volume', volume: Math.min(1, Math.max(0, volume)) });
    },

    setAudioRepeat(repeat) {
      socket.send({ type: 'audio.repeat', repeat });
    },

    triggerSfx(trackId) {
      socket.send({ type: 'audio.sfx', trackId });
    },

    sendSignal(targetUserId, signal) {
      socket.send({ type: 'rtc.signal', targetUserId, signal });
    },

    sendMedia(media) {
      socket.send({ type: 'rtc.media', media });
    },

    /* ---------------------------------------------------------------- contatos */

    async refreshContacts() {
      const token = get().token;
      if (!token) return;
      try {
        const { contacts } = await api.listContacts(token);
        // um servidor antigo não manda as "Outras conversas"
        set({ contacts: { ...contacts, others: contacts.others ?? [] } });
      } catch (error) {
        set({ error: describeError(error) });
      }
    },

    async searchUsers(query) {
      const token = get().token;
      if (!token) return;
      const termo = query.trim();
      const seq = ++searchSeq;
      if (termo.length === 0) {
        set({ userSearch: [] });
        return;
      }
      try {
        const { users } = await api.searchUsers(token, termo);
        if (seq !== searchSeq) return;
        set({ userSearch: users });
      } catch (error) {
        set({ error: describeError(error) });
      }
    },

    async requestContact(target, message) {
      const token = get().token;
      if (!token) return false;
      try {
        const { contact } = await api.requestContact(token, target, message);
        // a busca continua na tela, agora mostrando "pedido enviado"
        const userId = contact?.user.id ?? ('userId' in target ? target.userId : null);
        set((state) => ({ userSearch: state.userSearch.map((r) => (r.user.id === userId ? { ...r, relation: contact?.state ?? 'pending' } : r)) }));
        await get().refreshContacts();
        return true;
      } catch (error) {
        set({ error: describeError(error) });
        return false;
      }
    },

    async contactAction(userId, action) {
      const token = get().token;
      if (!token) return;
      try {
        await api.contactAction(token, userId, action);
        await get().refreshContacts();
      } catch (error) {
        set({ error: describeError(error) });
      }
    },

    openChat(userId, seed) {
      // quem abriu já sabe quem é a pessoa (o participante, o dono da mesa): o nome aparece já
      if (seed && !get().dmPeers[userId]) set((state) => ({ dmPeers: { ...state.dmPeers, [userId]: { user: seed, relation: null, access: 'ok' } } }));
      useFriendsUi.getState().openWindow(userId, { narrow: isMobileNow() });
      if (isMobileNow()) useFriendsUi.getState().setOpen(true);
      // o histórico, e de novo quando não dava para mandar (a pessoa pode ter mudado de ideia)
      const peer = get().dmPeers[userId];
      if (!get().conversations[userId]?.loaded || !peer || peer.access !== 'ok' || seed) void get().loadConversation(userId);
    },

    async loadConversation(userId) {
      const token = get().token;
      if (!token) return;
      const seq = (conversationSeq.get(userId) ?? 0) + 1;
      conversationSeq.set(userId, seq);
      try {
        const { messages, peer } = await api.listDirectMessages(token, userId);
        // um pedido mais novo da mesma conversa já saiu: este fica de fora
        if (conversationSeq.get(userId) !== seq) return;
        // o que chegou pelo aviso durante a busca continua (as duas listas se juntam)
        addMessages(userId, messages, true);
        if (peer) set((state) => ({ dmPeers: { ...state.dmPeers, [userId]: peer } }));
      } catch (error) {
        // a conta da pessoa não existe mais: a janela não tem o que mostrar
        if (error instanceof ApiError && error.status === 404) {
          useFriendsUi.getState().closeWindow(userId);
          return;
        }
        set({ error: describeError(error) });
      }
    },

    async markConversationRead(userId) {
      const token = get().token;
      if (!token) return;
      const contact = friendOf(userId) ?? get().contacts?.others.find((c) => c.user.id === userId);
      patchUnread(userId, () => 0);
      // nada a marcar (e a lista ainda não chegou): não precisa ir ao servidor
      if (contact && contact.unreadCount === 0) return;
      try {
        await api.markDirectMessagesRead(token, userId);
      } catch {
        // fica não lido no servidor; volta certo na próxima vez que a lista vier
      }
    },

    async sendDirectMessage(userId, text) {
      const token = get().token;
      const limpo = text.trim();
      if (!token || limpo.length === 0) return false;
      try {
        const { message } = await api.sendDirectMessage(token, userId, limpo);
        addMessages(userId, [message]);
        touchOther(userId, message, get().dmPeers[userId]?.user);
        return true;
      } catch (error) {
        // recusada (bloqueio, "só amigos"): a janela troca a caixa pelo aviso, em vez do erro solto
        const access = error instanceof ApiError && error.status === 403 ? REFUSED[error.code ?? ''] : undefined;
        const peer = get().dmPeers[userId];
        if (access && peer) {
          set((state) => ({ dmPeers: { ...state.dmPeers, [userId]: { ...peer, access } } }));
          return false;
        }
        set({ error: describeError(error) });
        return false;
      }
    },

    async dismissConversation(userId) {
      const token = get().token;
      if (!token) return;
      set((state) => (state.contacts ? { contacts: { ...state.contacts, others: state.contacts.others.filter((c) => c.user.id !== userId) } } : state));
      useFriendsUi.getState().closeWindow(userId);
      try {
        await api.dismissDirectConversation(token, userId);
      } catch (error) {
        set({ error: describeError(error) });
        void get().refreshContacts();
      }
    },

    async loadPrivacy() {
      const token = get().token;
      if (!token) return;
      try {
        const { privacy } = await api.getPrivacy(token);
        set({ privacy });
      } catch (error) {
        set({ error: describeError(error) });
      }
    },

    async setImsPrivacy(ims) {
      const token = get().token;
      const before = get().privacy;
      if (!token || !before) return false;
      set({ privacy: { ...before, ims } });
      try {
        const { privacy } = await api.setPrivacy(token, { ...before, ims });
        set({ privacy });
        return true;
      } catch (error) {
        set({ privacy: before, error: describeError(error) });
        return false;
      }
    },

    sendDmTyping(userId, typing) {
      if (!get().serverFeatures.presence || !socket.isOpen()) return;
      socket.send({ type: 'ims.typing', targetUserId: userId, typing });
    },

    reportAway(away) {
      if (lastAway === away) return;
      lastAway = away;
      if (get().serverFeatures.presence && socket.isOpen()) socket.send({ type: 'presence.away', away });
    },

    setMyStatus(status) {
      if (!get().serverFeatures.status || !socket.isOpen()) {
        toast(translate(useLanguage.getState().language, 'friends.me.notConnected'), 'error');
        return;
      }
      if (get().myStatus === status) return;
      // muda já; o servidor confirma (e avisa as minhas outras janelas) com `presence.status`
      set({ myStatus: status });
      socket.send({ type: 'presence.status', status });
    },

    async inviteFriends(userIds) {
      const { token, activeRoomId } = get();
      if (!token || !activeRoomId || !userIds.length) return 0;
      try {
        const { messages } = await api.inviteFriends(token, activeRoomId, userIds);
        for (const message of messages) addMessages(message.toId, [message]);
        return messages.length;
      } catch (error) {
        set({ error: describeError(error) });
        return 0;
      }
    },

    /* ---------------------------------------------------------------- perfil */

    closeProfile() {
      set({ profileOpen: false });
    },

    async viewProfile(userId, open = true) {
      const token = get().token;
      if (!token) return;
      // abre já (com o carregando) se for outra pessoa
      set((state) => ({ ...(open ? { profileOpen: true } : {}), ...(state.profileUserId !== userId ? { profile: null, profileUserId: userId } : {}) }));
      try {
        const { profile } = await api.getProfile(token, userId);
        // abriram outro perfil enquanto este carregava: a resposta atrasada fica de fora
        if (get().profileUserId !== userId) return;
        set((state) => ({ profile, profileUserId: userId, ...(userId === state.user?.id ? { myProfile: profile } : {}) }));
      } catch (error) {
        // sem o perfil, o popup não fica no "carregando" para sempre: fecha, com o aviso
        set((state) => ({ error: describeError(error), ...(state.profileUserId === userId ? { profileOpen: false, profileUserId: null } : {}) }));
      }
    },

    async loadMyProfile() {
      const { token, user } = get();
      if (!token || !user) return;
      try {
        const { profile } = await api.getProfile(token, user.id);
        // saiu ou trocou de conta enquanto carregava
        if (get().user?.id !== user.id) return;
        set({ myProfile: profile });
      } catch (error) {
        set({ error: describeError(error) });
      }
    },

    async saveProfile(patch) {
      const token = get().token;
      if (!token) return false;
      try {
        const { profile } = await api.updateProfile(token, patch);
        // o popup só troca se estiver no meu perfil (o de outra pessoa continua)
        set((state) => ({ myProfile: profile, ...(state.profileUserId === profile.user.id ? { profile } : {}) }));
        return true;
      } catch (error) {
        set({ error: describeError(error) });
        return false;
      }
    },

    async voteUser(userId, value) {
      const token = get().token;
      if (!token) return;
      try {
        const { profile } = await api.voteUser(token, userId, value);
        set((state) => (state.profileUserId === userId ? { profile } : state));
      } catch (error) {
        set({ error: describeError(error) });
      }
    },

    /* ---------------------------------------------------------------- configurações */

    updateSettings(patch) {
      const proximas: AppSettings = { ...get().settings, ...patch };
      saveSettings(proximas);
      applyGlobalSettings(proximas);
      // escolher a textura de novo (a mesma, restaurada da lixeira, ou outra) tira o aviso da recusa
      set({ settings: proximas, ...('diceTexture' in patch ? { diceTextureRefused: null } : {}) });
    },

    /* ---------------------------------------------------------------- Desk Cloud */

    async refreshFiles() {
      const token = get().token;
      if (!token) return;
      try {
        const { usage } = await api.fileSummary(token);
        set({ fileUsage: { used: usage.used, quota: usage.quota } });
      } catch (error) {
        set({ error: describeError(error) });
      }
    },

    async setAvatar(image) {
      const token = get().token;
      if (!token) return false;
      try {
        const { user } = image ? await api.setAvatar(token, image) : await api.removeAvatar(token);
        set({ user });
        return true;
      } catch (error) {
        set({ error: describeError(error) });
        return false;
      }
    },

    async setRoomAvatar(userId, image) {
      const { token, room } = get();
      if (!token || !room) return false;
      try {
        if (image) await api.setRoomAvatar(token, room.id, userId, image);
        else await api.removeRoomAvatar(token, room.id, userId);
        return true;
      } catch (error) {
        set({ error: describeError(error) });
        return false;
      }
    },

    async deleteRoom(roomId) {
      const token = get().token;
      if (!token) return;
      try {
        await api.deleteRoom(token, roomId);
        set((state) => ({ rooms: state.rooms.filter((room) => room.id !== roomId) }));
        if (get().activeRoomId === roomId) get().leaveRoom();
        await get().refreshFiles();
      } catch (error) {
        set({ error: describeError(error) });
      }
    },

    async changePassword(currentPassword, newPassword) {
      const token = get().token;
      if (!token) return false;
      set({ busy: true, error: null });
      try {
        await api.changePassword(token, currentPassword, newPassword);
        return true;
      } catch (error) {
        set({ error: describeError(error) });
        return false;
      } finally {
        set({ busy: false });
      }
    },

    async deleteAccount(password) {
      const token = get().token;
      if (!token) return false;
      set({ busy: true, error: null });
      try {
        await api.deleteAccount(token, password);
        // a conta morreu: limpa tudo, como ao sair, e volta para a tela de entrada
        forgetAccount();
        return true;
      } catch (error) {
        set({ error: describeError(error) });
        return false;
      } finally {
        set({ busy: false });
      }
    },

    dismissError() {
      set({ error: null });
    },
  };
});

// depuração e testes ponta a ponta: o estado fica acessível no console em desenvolvimento
if (import.meta.env.DEV) (globalThis as { __rpgDeskStore?: unknown }).__rpgDeskStore = useApp;

// trocar a língua da interface troca os nomes dos plugins que têm tradução
useLanguage.subscribe((state) => useApp.setState((app) => ({ plugins: localizePlugins(app.pluginsRaw, state.language) })));

// as linhas do painel de macros entre as janelas da mesa (room/chatRun.ts): a que tem o chat principal
// roda a pedida noutra; quem pediu esquece a sua quando outra rodou
onWindowMessage((message) => {
  const { chatRun, room } = useApp.getState();
  if (message.t === 'roll-shown') {
    // na janela própria: os dados pararam na principal (ou ela não anima); o aviso que chega antes da
    // rolagem fica guardado, para ela não esperar à toa
    if (!POPOUT || room?.id !== message.roomId) return;
    if (held.has(message.rollId)) useApp.getState().revealRoll(message.rollId);
    else {
      shownByMain.add(message.rollId);
      if (shownByMain.size > MAX_ROLLS) shownByMain.delete(shownByMain.values().next().value!);
    }
  } else if (message.t === 'run-chat') {
    if (!mainChatHere() || room?.id !== message.roomId || chatRun?.id === message.runId) return;
    useApp.setState((state) => ({ chatRun: { id: message.runId, roomId: message.roomId, text: message.text, nonce: (state.chatRun?.nonce ?? 0) + 1, at: Date.now(), done: false } }));
  } else if (message.t === 'chat-ran' && chatRun?.id === message.runId && !chatRun.done) {
    useApp.setState({ chatRun: { ...chatRun, done: true } });
  }
});
